import React from 'react';
import * as shapefile from 'shapefile';
import proj4 from 'proj4';
import { bearing, booleanContains, booleanOverlap, booleanPointInPolygon, distance, point } from '@turf/turf';
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputAdornment,
    InputLabel,
    LinearProgress,
    Menu,
    MenuItem,
    Paper,
    Radio,
    Select,
    SvgIcon,
    TextField,
    Typography
} from '@material-ui/core';
import { notification } from 'antd';
import SearchIcon from '@material-ui/icons/Search';
import L from 'leaflet';
import 'leaflet-draw';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import saveAs from 'file-saver';
import Config from '../../constants/appConfig';
import MaterialIcon from '../../components/MaterialIcon';
import Tools from '../../components/util/tools';
import Units from '../../components/util/units';
import readGeoFile from '../../components/geo';
import generateFlightPath2D from '../../utils/flightPath2D';
import toolsPlan from '../job_estimate/tools';
import iconA from '../../assets/images/marker/a.svg';
import iconB from '../../assets/images/marker/b.svg';
import baseIcon from '../../assets/images/marker/waypoint/hangar.png';
import runwayIcon from '../../assets/images/marker/waypoint/marker-icon-green.png';
import './NewPlanning.css';

const tokenHeaders = () => ({ Authorization: localStorage.getItem('access-token-jwt') });
const planningPointIcon = (isBase) => L.icon({
    iconUrl: isBase ? baseIcon : runwayIcon,
    iconSize: isBase ? [32, 32] : [25, 41],
    iconAnchor: isBase ? [16, 16] : [12, 41],
    tooltipAnchor: isBase ? [0, -16] : [0, -38]
});
const IMPORTED_CLIENT_ID = '__imported__';
const isApplicationArea = (area) => {
    const type = String(area.type || 'N').toUpperCase();
    return !type.startsWith('X') && type !== 'T' && type !== 'R';
};
const DEFAULT_SWATH_WIDTH_METERS = 20;
const defaultSwathWidth = (res) => String(Number(
    Units.convertLength(DEFAULT_SWATH_WIDTH_METERS, 'm', res).toFixed(2)
));
const MEDIUM_RENDER_ZOOM = 12;
const MEDIUM_RENDER_LIMIT = 700;
const MEDIUM_VIEWPORT_PADDING = 0.25;
const MEDIUM_CLUSTER_GRID_SIZE = 80;
const MEDIUM_RENDER_BATCH_SIZE = 6;
const POLYGON_PREVIEW_LINE_LIMIT = 7;
const AREA_SELECTION_ICONS = {
    rectangle: 'M 99 19 L 99 39 L 78 39 L 78 19 z M 97 34 L 99 34 L 99 30 L 97 30 L 97 28 L 99 28 L 99 24 L 97 24 L 97 21 L 94 21 L 94 19 L 90 19 L 90 21 L 87 21 L 87 19 L 83 19 L 83 21 L 80 21 L 80 24 L 78 24 L 78 28 L 80 28 L 80 30 L 78 30 L 78 34 L 80 34 L 80 37 L 83 37 L 83 39 L 87 39 L 87 37 L 90 37 L 90 39 L 94 39 L 94 37 L 97 37 z',
    lasso: 'M 100.003 22.997 L 97 36.9 L 83 40 L 76 27 L 86 18 z M 96.109 31.635 L 98.032 32.276 L 99.037 27.914 L 97.244 27.441 L 98.012 24.052 L 93.677 22.653 L 94.387 21.017 L 89.851 19.328 L 89.103 20.931 L 86.352 19.81 L 84 22 L 82.872 20.815 L 79.821 23.569 L 80.914 24.761 L 78.146 27.234 L 80.103 31.055 L 78.578 31.723 L 80.773 35.814 L 81.876 35.026 L 83.672 38.046 L 85.935 37.489 L 86.25 39.282 L 91.621 38.046 L 91.033 36.229 L 95.53 35.204 z'
};
const PLANNING_STRATEGIES = [
    { type: 'BACKTOBACK', labelKey: 'ESTRATEGIA_BACK_TO_BACK' },
    { type: 'SKIPBACKTOBACK', labelKey: 'ESTRATEGIA_SKIP_BACK_TO_BACK' },
    { type: 'RACETRACK', labelKey: 'ESTRATEGIA_RACETRACK' },
    { type: 'QUICKRACETRACK', labelKey: 'ESTRATEGIA_QUICK_RACETRACK' },
    { type: 'REVERSERACETRACK', labelKey: 'ESTRATEGIA_REVERSE_RACETRACK' },
    { type: 'EXPAND', labelKey: 'ESTRATEGIA_EXPAND' },
    { type: 'SQUEEZE', labelKey: 'ESTRATEGIA_SQUEEZE' }
];
const GENERATION_STAGES = [
    { type: 'prepare', label: 'PLANEJAMENTO_ETAPA_PREPARAR' },
    { type: 'merge', label: 'PLANEJAMENTO_ETAPA_MERGE' },
    { type: 'strategy', label: 'PLANEJAMENTO_ETAPA_ESTRATEGIAS' },
    { type: 'estimate', label: 'PLANEJAMENTO_ETAPA_ESTIMATIVA' },
    { type: 'totals', label: 'PLANEJAMENTO_ETAPA_TOTAIS' },
    { type: 'finalize', label: 'PLANEJAMENTO_ETAPA_FINALIZAR' },
    { type: 'complete', label: 'PLANEJAMENTO_ETAPA_CONCLUIDO' }
];

const planningRequest = async (url, options, onProgress, errorMessage) => {
    const response = await fetch(url, options);
    if (!response.ok) {
        let message = errorMessage;
        try { message = (await response.json()).message || message; } catch (_) { }
        throw new Error(message);
    }
    if (!(response.headers.get('Content-Type') || '').includes('application/x-ndjson')) return response.json();
    let result;
    let receivedResult = false;
    const consume = (line) => {
        if (!line.trim()) return;
        const event = JSON.parse(line);
        if (event.type === 'progress') onProgress(event);
        if (event.type === 'error') throw new Error(event.message || errorMessage);
        if (event.type === 'result') { result = event.data; receivedResult = true; }
    };
    if (response.body && response.body.getReader) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let pending = '';
        try {
            while (true) {
                const { value, done } = await reader.read();
                pending += done ? decoder.decode() : decoder.decode(value, { stream: true });
                let newline;
                while ((newline = pending.indexOf('\n')) >= 0) {
                    consume(pending.slice(0, newline));
                    pending = pending.slice(newline + 1);
                }
                if (done) break;
            }
            consume(pending);
        } finally {
            reader.releaseLock();
        }
    } else {
        (await response.text()).split('\n').forEach(consume);
    }
    if (!receivedResult || !result) throw new Error(errorMessage);
    return result;
};

const jsonRequest = async (url, options) => {
    const response = await fetch(url, options);
    if (!response.ok) {
        let message = '';
        let code;
        try {
            const error = await response.json();
            message = error.message || error.error || '';
            code = error.code;
        } catch (_) { }
        const error = new Error(message || `Falha na solicitação (${response.status})`);
        error.status = response.status;
        error.code = code;
        throw error;
    }
    return response.json();
};

const parsePoints = (value) => {
    if (typeof value === 'string') {
        try { return JSON.parse(value); } catch (_) { return null; }
    }
    return value;
};

const areaToFeature = (area) => {
    let points = parsePoints(area && area.points);
    if (!points) return null;
    if (points.type === 'FeatureCollection') points = points.features && points.features[0];
    if (points && points.type === 'Feature' && points.geometry) return points;
    if (points && points.type === 'Polygon') {
        return { type: 'Feature', properties: {}, geometry: points };
    }
    if (points && points.type === 'MultiPolygon') {
        return { type: 'Feature', properties: {}, geometry: points };
    }
    if (points && points.coordinates && points.type) return null;

    let ring = points;
    if (Array.isArray(ring) && Array.isArray(ring[0]) && Array.isArray(ring[0][0])) ring = ring[0];
    if (!Array.isArray(ring) || ring.length < 3) return null;
    const coordinates = ring.map((point) => {
        if (Array.isArray(point)) return [Number(point[1]), Number(point[0])];
        if (point && point.lat !== undefined && point.lng !== undefined) return [Number(point.lng), Number(point.lat)];
        return [NaN, NaN];
    });
    if (coordinates.some((point) => !Number.isFinite(point[0]) || !Number.isFinite(point[1]))) return null;
    const first = coordinates[0];
    const last = coordinates[coordinates.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) coordinates.push(first.slice());
    return {
        type: 'Feature',
        properties: { name: area.name || '' },
        geometry: { type: 'Polygon', coordinates: [coordinates] }
    };
};

const ringAreaSquareMeters = (ring) => {
    if (!Array.isArray(ring) || ring.length < 3) return 0;
    const earthRadius = 6378137;
    const radians = Math.PI / 180;
    let area = 0;
    ring.forEach((point, index) => {
        const next = ring[(index + 1) % ring.length];
        const longitude = Number(point && point[0]);
        const latitude = Number(point && point[1]);
        const nextLongitude = Number(next && next[0]);
        const nextLatitude = Number(next && next[1]);
        if (![longitude, latitude, nextLongitude, nextLatitude].every(Number.isFinite)) return;
        area += (nextLongitude - longitude) * radians * (
            2 + Math.sin(latitude * radians) + Math.sin(nextLatitude * radians)
        );
    });
    return Math.abs(area * earthRadius * earthRadius / 2);
};

const featureAreaHectares = (feature) => {
    let parsed = parsePoints(feature);
    if (parsed && parsed.type === 'FeatureCollection') parsed = parsed.features && parsed.features[0];
    const geometry = parsed && parsed.type === 'Feature' ? parsed.geometry : parsed;
    if (!geometry || !Array.isArray(geometry.coordinates)) return null;
    const polygonArea = (coordinates) => {
        if (!Array.isArray(coordinates) || !coordinates.length) return 0;
        return Math.max(0, ringAreaSquareMeters(coordinates[0]) - coordinates.slice(1).reduce(
            (holesArea, ring) => holesArea + ringAreaSquareMeters(ring), 0
        ));
    };
    const areaSquareMeters = geometry.type === 'Polygon'
        ? polygonArea(geometry.coordinates)
        : geometry.type === 'MultiPolygon'
            ? geometry.coordinates.reduce((total, coordinates) => total + polygonArea(coordinates), 0)
            : NaN;
    return Number.isFinite(areaSquareMeters) ? areaSquareMeters / 10000 : null;
};

const fileExtension = (file) => String(file && file.name || '').split('.').pop().toLowerCase();

const fileStem = (file) => String(file && file.name || '')
    .replace(/\\/g, '/')
    .split('/')
    .pop()
    .replace(/\.[^.]+$/, '')
    .toLowerCase();

const transformShapeCoordinates = (coordinates, transformer) => {
    if (!Array.isArray(coordinates)) return coordinates;
    if (coordinates.length >= 2 && typeof coordinates[0] === 'number' && typeof coordinates[1] === 'number') {
        const transformed = transformer.forward([coordinates[0], coordinates[1]]);
        if (!Number.isFinite(transformed[0]) || !Number.isFinite(transformed[1]) ||
            transformed[0] < -180 || transformed[0] > 180 || transformed[1] < -90 || transformed[1] > 90) {
            throw new Error('Invalid shapefile projection');
        }
        return coordinates.length > 2 ? [transformed[0], transformed[1], ...coordinates.slice(2)] : transformed;
    }
    return coordinates.map((item) => transformShapeCoordinates(item, transformer));
};

const coordinatesAreGeographic = (coordinates) => {
    if (!Array.isArray(coordinates)) return true;
    if (coordinates.length >= 2 && typeof coordinates[0] === 'number' && typeof coordinates[1] === 'number') {
        return Number.isFinite(coordinates[0]) && Number.isFinite(coordinates[1]) &&
            coordinates[0] >= -180 && coordinates[0] <= 180 && coordinates[1] >= -90 && coordinates[1] <= 90;
    }
    return coordinates.every(coordinatesAreGeographic);
};

const featureBounds = (feature) => {
    let minLat = Infinity;
    let minLng = Infinity;
    let maxLat = -Infinity;
    let maxLng = -Infinity;
    const visitCoordinates = (coordinates) => {
        if (!Array.isArray(coordinates)) return;
        if (coordinates.length >= 2 && typeof coordinates[0] === 'number' && typeof coordinates[1] === 'number') {
            const [lng, lat] = coordinates;
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
            minLat = Math.min(minLat, lat);
            minLng = Math.min(minLng, lng);
            maxLat = Math.max(maxLat, lat);
            maxLng = Math.max(maxLng, lng);
            return;
        }
        coordinates.forEach(visitCoordinates);
    };
    const visitGeometry = (geometry) => {
        if (!geometry) return;
        if (geometry.type === 'GeometryCollection') {
            (geometry.geometries || []).forEach(visitGeometry);
            return;
        }
        visitCoordinates(geometry.coordinates);
    };

    visitGeometry(feature && feature.type === 'Feature' ? feature.geometry : feature);
    if (!Number.isFinite(minLat) || !Number.isFinite(minLng) || !Number.isFinite(maxLat) || !Number.isFinite(maxLng)) return null;
    return L.latLngBounds([[minLat, minLng], [maxLat, maxLng]]);
};

const PlanningAreaSelectionRow = React.memo(({
    area,
    id,
    selected,
    flow,
    defaultFlow,
    singleAreaOnly,
    areaLabel,
    flowLabel,
    zoneType,
    zoneLabel,
    onSelectionChange,
    onFlowChange
}) => (
    <div className={`new-planning-area-row${zoneType ? ' new-planning-area-row-zone' : ''}`}>
        <FormControlLabel
            control={singleAreaOnly
                ? <Radio color="primary" name="new-planning-area" value={id} checked={selected} onChange={() => onSelectionChange(area.id, true)} />
                : <Checkbox color="primary" checked={selected} onChange={(event) => onSelectionChange(area.id, event.target.checked)} />}
            label={
                <span className="new-planning-area-name">{area.name || `${areaLabel} ${area.id}`}</span>
            }
        />
        {zoneType && (
            <span
                className={`new-planning-area-zone new-planning-area-zone-${zoneType.toLowerCase()}`}
                title={zoneLabel}
                aria-label={zoneLabel}
            >
                {zoneType}
            </span>
        )}
        {selected && !zoneType && <TextField type="number" variant="outlined" size="small" label={flowLabel} value={flow || defaultFlow} onChange={(event) => onFlowChange(id, event.target.value)} inputProps={{ min: 0.1, step: 0.1 }} />}
    </div>
));

const readShapeFile = async (file, files) => {
    const stem = fileStem(file);
    const relatedFile = (extension) => files.find((candidate) =>
        candidate !== file && fileStem(candidate) === stem && fileExtension(candidate) === extension
    );
    const dbfFile = relatedFile('dbf');
    const prjFile = relatedFile('prj');
    const cpgFile = relatedFile('cpg');
    const [shpBuffer, dbfBuffer, prjText, cpgText] = await Promise.all([
        file.arrayBuffer(),
        dbfFile ? dbfFile.arrayBuffer() : Promise.resolve(null),
        prjFile ? prjFile.text() : Promise.resolve(''),
        cpgFile ? cpgFile.text() : Promise.resolve('')
    ]);
    const options = {};
    const encoding = String(cpgText || '').trim();
    if (encoding) options.encoding = encoding === '65001' ? 'utf-8' : (/^\d+$/.test(encoding) ? `windows-${encoding}` : encoding);
    const collection = await shapefile.read(shpBuffer, dbfBuffer, options);
    const projection = String(prjText || '').trim();
    const usesAssumedGeographic = /GCS[_\s]+Assumed[_\s]+Geographic/i.test(projection);
    const transformer = projection && !usesAssumedGeographic ? proj4(projection, 'WGS84') : null;
    return (collection.features || []).filter((feature) => feature && feature.geometry).map((feature) => {
        const geometry = Object.assign({}, feature.geometry);
        if (transformer) geometry.coordinates = transformShapeCoordinates(geometry.coordinates, transformer);
        else if (!coordinatesAreGeographic(geometry.coordinates)) throw new Error('Invalid shapefile coordinates');
        return Object.assign({}, feature, { geometry });
    });
};

const polygonsFromFile = async (file, files) => {
    const extension = fileExtension(file);
    let features;
    if (extension === 'shp') {
        features = await readShapeFile(file, files);
    } else if (extension === 'json' || extension === 'geojson') {
        const data = JSON.parse(await file.text());
        if (data.type === 'FeatureCollection') features = data.features || [];
        else if (data.type === 'Feature') features = [data];
        else features = [{ type: 'Feature', properties: {}, geometry: data }];
    } else {
        features = await readGeoFile(file);
    }
    return (features || []).filter((feature) => feature && feature.geometry &&
        ['Polygon', 'MultiPolygon'].indexOf(feature.geometry.type) >= 0);
};

const featureLabel = (feature, filename, index) => {
    const properties = feature && feature.properties || {};
    const preferredKeys = ['name', 'nome', 'title', 'titulo', 'id', 'codigo'];
    const key = Object.keys(properties).find((property) => preferredKeys.indexOf(property.toLowerCase()) >= 0) ||
        Object.keys(properties).find((property) => properties[property] !== null && properties[property] !== undefined && String(properties[property]).trim());
    return key && String(properties[key]).trim() ? String(properties[key]).trim() : `${filename} ${index + 1}`;
};

const normalizeSearchText = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase();

const formatDuration = (rawSeconds) => {
    if (rawSeconds === undefined || rawSeconds === null || !Number.isFinite(Number(rawSeconds))) return '—';
    const totalMinutes = Math.max(0, Math.round((Number(rawSeconds) || 0) / 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) return `${hours}h ${minutes}min`;
    return `${minutes} min`;
};

const getPolygonRings = (feature) => {
    const geometry = feature && feature.geometry ? feature.geometry : feature;
    if (!geometry || !Array.isArray(geometry.coordinates)) return [];
    if (geometry.type === 'Polygon') return [geometry.coordinates];
    if (geometry.type === 'MultiPolygon') return geometry.coordinates;
    return [];
};

const getLineSegments = (value) => {
    const segments = [];
    const visit = (item) => {
        if (Array.isArray(item)) {
            const isLine = item.length >= 2 && item.every((point) => Array.isArray(point) &&
                Number.isFinite(Number(point[0])) && Number.isFinite(Number(point[1])));
            if (isLine) {
                segments.push(item);
                return;
            }
            item.forEach(visit);
            return;
        }
        if (!item || typeof item !== 'object') return;
        if (item.type === 'Feature') return visit(item.geometry);
        if (item.type === 'FeatureCollection') return (item.features || []).forEach(visit);
        if (item.type === 'GeometryCollection') return (item.geometries || []).forEach(visit);
        if (item.type === 'LineString') return visit(item.coordinates);
        if (item.type === 'MultiLineString') return (item.coordinates || []).forEach(visit);
        if (item.lines) return visit(item.lines);
        if (item.linhas) return visit(item.linhas);
        if (item.line) return visit(item.line);
        if (item.geometry) return visit(item.geometry);
        if (item.coordinates) visit(item.coordinates);
    };
    visit(value);
    return segments;
};

const getPolygonLineGroups = (candidate) => {
    const strips = candidate && Array.isArray(candidate.lines) ? candidate.lines : [];
    const groups = strips.map((strip, index) => ({
        index,
        key: Number(strip && strip.key),
        segments: getLineSegments(strip)
    })).filter((group) => group.segments.length > 0);
    if (groups.every((group) => Number.isFinite(group.key))) groups.sort((left, right) => left.key - right.key);
    return groups.map((group) => group.segments);
};

const getFlightPasses = (candidate, preserveOrder = false) => {
    const strips = candidate && Array.isArray(candidate.lines) ? candidate.lines : [];
    const groups = strips.map((strip, index) => {
        const key = Number(strip && strip.key);
        return {
            index,
            key: Number.isFinite(key) ? key : index,
            segments: getLineSegments(strip)
        };
    }).filter((group) => group.segments.length > 0);
    if (!preserveOrder && groups.every((group) => Number.isFinite(group.key))) groups.sort((left, right) => left.key - right.key);
    return groups.reduce((passes, group) => passes.concat(group.segments.map((coordinates) => ({
        key: group.key,
        coordinates
    }))), []);
};

const getPolygonStrategyPlan = (polygon, strategy, swathWidthM) => {
    if (!polygon) return { pattern: null, ab: null };
    const candidate = polygon.lines;
    const abs = candidate && candidate.ABs;
    const pattern = abs && toolsPlan.createPlanningPattern(
        candidate.lines, abs, polygon.selectedABBack, polygon.selectedAB, strategy,
        { size_loop: polygon.loopWidthM, size_line: swathWidthM }
    );
    const reference = (pattern && pattern.ab) || (abs && (polygon.selectedABBack === 1
        ? (abs.mirroredAB || abs.endAB)
        : (abs.initialAB || abs.startAB)));
    const coordinates = reference && Array.isArray(reference.c)
        ? (polygon.selectedAB === 1 ? reference.c : reference.c.slice().reverse())
        : polygon.ab;
    if (!Array.isArray(coordinates) || coordinates.length < 2) return { pattern, ab: null };
    const endpoints = coordinates.slice(0, 2);
    if (!endpoints.every((point) => Array.isArray(point) && point.length >= 2 &&
        point[0] !== null && point[1] !== null &&
        Number.isFinite(Number(point[0])) && Number.isFinite(Number(point[1])) &&
        Math.abs(Number(point[0])) <= 180 && Math.abs(Number(point[1])) <= 90)) return { pattern, ab: null };
    return { pattern, ab: endpoints.map((point) => [Number(point[0]), Number(point[1])]) };
};

const sampleLineGroups = (groups) => {
    if (groups.length <= POLYGON_PREVIEW_LINE_LIMIT) return groups;
    const step = Math.ceil((groups.length - 1) / (POLYGON_PREVIEW_LINE_LIMIT - 1));
    const sampleCount = Math.floor((groups.length - 1) / step) + 1;
    const offset = Math.round((groups.length - 1 - (sampleCount - 1) * step) / 2);
    return Array.from({ length: sampleCount }, (_, index) => groups[offset + index * step]);
};

const PolygonPreview = ({ feature, lines, name }) => {
    const polygons = getPolygonRings(feature)
        .map((rings) => (Array.isArray(rings) ? rings : []).map((ring) =>
            (Array.isArray(ring) ? ring : []).filter((point) => Array.isArray(point) &&
                Number.isFinite(Number(point[0])) && Number.isFinite(Number(point[1])))
        ).filter((ring) => ring.length >= 3))
        .filter((rings) => rings.length > 0);
    const lineGroups = getPolygonLineGroups(lines).map((group) => group.map((line) => line.filter((point) => Array.isArray(point) &&
        Number.isFinite(Number(point[0])) && Number.isFinite(Number(point[1])))).filter((line) => line.length >= 2));
    const lineSegments = lineGroups.reduce((all, group) => all.concat(group), []);
    const points = polygons.reduce((all, rings) => all.concat(...rings), []).concat(...lineSegments);
    if (!points.length) return <div className="new-planning-polygon-preview" aria-hidden="true" />;

    const bounds = points.reduce((result, point) => ({
        minX: Math.min(result.minX, Number(point[0])),
        maxX: Math.max(result.maxX, Number(point[0])),
        minY: Math.min(result.minY, Number(point[1])),
        maxY: Math.max(result.maxY, Number(point[1]))
    }), { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity });
    const width = bounds.maxX - bounds.minX || 1;
    const height = bounds.maxY - bounds.minY || 1;
    const scale = Math.min(84 / width, 84 / height);
    const offsetX = (100 - width * scale) / 2;
    const offsetY = (100 - height * scale) / 2;
    const previewLineGroups = sampleLineGroups(lineGroups);
    const coordinatesToPath = (coordinates, closePath) => coordinates.map((point, index) => {
        const x = offsetX + (Number(point[0]) - bounds.minX) * scale;
        const y = 100 - offsetY - (Number(point[1]) - bounds.minY) * scale;
        return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    }).join(' ') + (closePath ? ' Z' : '');
    const paths = polygons.map((rings) => rings.map((ring) => coordinatesToPath(ring, true)).join(' '));
    const linePaths = previewLineGroups.reduce((all, group) => all.concat(group), [])
        .map((line) => coordinatesToPath(line, false));

    return (
        <div className="new-planning-polygon-preview">
            <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" role="img" aria-label={name || undefined}>
                {paths.map((path, index) => (
                    <path key={index} d={path} fill="#e7be37" fillOpacity="0.24" fillRule="evenodd" stroke="#9a7c15" strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
                ))}
                {linePaths.map((path, index) => (
                    <path key={`line-${index}`} d={path} fill="none" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                ))}
            </svg>
        </div>
    );
};

class PolygonFlightPathMap extends React.Component {
    constructor(props) {
        super(props);
        this.mapElement = null;
        this.map = null;
        this.layers = null;
        this.contextLayers = null;
        this.contextPassLayers = [];
        this.passLayers = [];
        this.flightPathLayer = null;
        this.polygonLayer = null;
        this.generatingFlightPath = false;
        this.renderToken = 0;
        this.abDrawingLayers = null;
        this.pendingABPoint = null;
        this.abLineLayers = [];
        this.abMarkers = [];
        this.draftABLineLayers = [];
        this.draftABEndMarker = null;
        this.draggingAB = false;
    }

    componentDidMount() {
        if (!this.mapElement) return;
        this.map = L.map(this.mapElement, { zoomControl: true, scrollWheelZoom: true }).setView([-15.78, -47.93], 4);
        this.map.attributionControl.setPrefix(false);
        L.tileLayer(Config.tile_google, Object.assign({ maxZoom: 21 }, Config.tile_google_config || {})).addTo(this.map);
        L.control.scale({ metric: true, imperial: false }).addTo(this.map);
        [
            ['context', 400], ['context-application', 405], ['polygon', 410], ['application', 420],
            ['path', 430], ['trim', 700], ['exclusion', 710]
        ].forEach(([name, zIndex]) => {
            this.map.createPane(`new-planning-flight-${name}`).style.zIndex = String(zIndex);
        });
        this.map.createPane('new-planning-ab').style.zIndex = '650';
        this.contextLayers = L.featureGroup().addTo(this.map);
        this.layers = L.featureGroup().addTo(this.map);
        this.abDrawingLayers = L.featureGroup().addTo(this.map);
        this.map.on('click', this.handleABMapClick);
        this.map.on('mousemove', this.handleABMapMove);
        this.map.on('zoomend moveend', this.setPassWidths);
        this.drawContextPolygons();
        this.drawFlightPath();
        setTimeout(() => {
            if (!this.map) return;
            this.map.invalidateSize();
            this.fitMap();
        }, 250);
    }

    componentDidUpdate(previousProps) {
        if (previousProps.selectingAB !== this.props.selectingAB) {
            this.clearABDrawing();
            this.updateABEditingState();
        }
        if (previousProps.editingDisabled !== this.props.editingDisabled) this.updateABEditingState();
        const previousPolygon = previousProps.polygon || {};
        const polygon = this.props.polygon || {};
        if (previousProps.contextPolygons !== this.props.contextPolygons || previousProps.contextDrafts !== this.props.contextDrafts ||
            previousProps.jobId !== this.props.jobId || previousPolygon.id !== polygon.id) {
            this.drawContextPolygons();
        }
        const geometryChanged = ['feature', 'lines', 'ab', 'selectedAB', 'selectedABBack', 'lineIndex', 'loopWidthM', 'loopLengthM']
            .some((key) => previousPolygon[key] !== polygon[key]);
        if (geometryChanged ||
            previousProps.strategy !== this.props.strategy ||
            previousProps.swathWidthM !== this.props.swathWidthM ||
            previousProps.base !== this.props.base ||
            previousProps.runways !== this.props.runways) {
            this.drawFlightPath();
        }
    }

    componentWillUnmount() {
        this.renderToken += 1;
        if (this.map) this.map.remove();
        this.map = null;
        this.layers = null;
        this.contextLayers = null;
        this.contextPassLayers = [];
        this.flightPathLayer = null;
        this.polygonLayer = null;
        this.generatingFlightPath = false;
        this.abDrawingLayers = null;
        this.pendingABPoint = null;
        this.abLineLayers = [];
        this.abMarkers = [];
        this.draftABLineLayers = [];
        this.draftABEndMarker = null;
        this.draggingAB = false;
    }

    setMapElement = (element) => {
        this.mapElement = element;
    };

    drawContextPolygons = () => {
        if (!this.contextLayers) return;
        this.contextLayers.clearLayers();
        this.contextPassLayers = [];
        (this.props.contextPolygons || []).forEach(({ polygon: sourcePolygon, type, jobId }) => {
            const pending = (this.props.contextDrafts || {})[`${jobId}:${sourcePolygon.id}`];
            const polygon = pending ? pending.polygon : sourcePolygon;
            if (!polygon.feature || (jobId === this.props.jobId && String(polygon.id) === String(this.props.polygon.id))) return;
            const exclusion = type === 'X' || type === 'XL';
            const trim = type === 'T' || type === 'R';
            const zoneColor = type === 'R' ? '#8e44ad' : trim ? '#facc15' : '#fb923c';
            const layer = L.geoJSON(polygon.feature, {
                pane: `new-planning-flight-${exclusion ? 'exclusion' : trim ? 'trim' : 'context'}`,
                interactive: !exclusion && !trim,
                style: {
                    color: exclusion ? '#dc2626' : zoneColor,
                    weight: exclusion || trim ? 2 : 1.5,
                    opacity: exclusion || trim ? 1 : 0.45,
                    fillColor: exclusion ? '#dc2626' : zoneColor,
                    fillOpacity: exclusion || trim ? 0.5 : 0.1
                },
                onEachFeature: (_feature, featureLayer) => {
                    if (exclusion || trim) return;
                    featureLayer.on('click', () => {
                        if (!this.props.selectingAB && !this.props.editingDisabled && this.props.onPolygonSelect) {
                            this.props.onPolygonSelect(jobId, polygon.id);
                        }
                    });
                }
            });
            if (polygon.name) {
                const label = document.createElement('span');
                label.textContent = polygon.name;
                layer.bindTooltip(label);
            }
            layer.addTo(this.contextLayers);
            if (!exclusion && !trim) {
                const passes = getFlightPasses(polygon.lines);
                if (passes.length) {
                    const application = L.polyline(passes.map((pass) => pass.coordinates.map(([lng, lat]) => [lat, lng])), {
                        pane: 'new-planning-flight-context-application',
                        interactive: false,
                        color: '#16a34a',
                        opacity: 0.3,
                        weight: 5,
                        lineCap: 'butt'
                    }).addTo(this.contextLayers);
                    this.contextPassLayers.push({ layer: application, swathWidthM: polygon.swathWidthM });
                }
            }
        });
        this.setPassWidths();
    };

    createABMarker = (latLng, index, options = {}) => {
        const label = index === 0 ? this.props.pointALabel : this.props.pointBLabel;
        return L.marker(latLng, {
            pane: 'new-planning-ab',
            title: label,
            alt: label,
            icon: L.icon({
                iconUrl: index === 0 ? iconA : iconB,
                iconSize: [40, 40],
                iconAnchor: [20, 35],
                className: 'new-planning-ab-marker'
            }),
            ...options
        });
    };

    createABLine = (latLngs, group) => [
        L.polyline(latLngs, { pane: 'new-planning-ab', color: '#243447', weight: 8, opacity: 0.9, interactive: false }).addTo(group),
        L.polyline(latLngs, { pane: 'new-planning-ab', color: '#facc15', weight: 4, opacity: 1, interactive: false }).addTo(group)
    ];

    clearABDrawing = () => {
        this.pendingABPoint = null;
        this.draftABLineLayers = [];
        this.draftABEndMarker = null;
        if (this.abDrawingLayers) this.abDrawingLayers.clearLayers();
    };

    updateABEditingState = () => {
        const opacity = this.props.selectingAB ? 0.25 : 1;
        this.abLineLayers.forEach((layer) => layer.setStyle({ opacity }));
        this.abMarkers.forEach((marker) => {
            marker.setOpacity(opacity);
            if (marker.dragging) {
                if (this.props.selectingAB || this.props.editingDisabled) marker.dragging.disable();
                else marker.dragging.enable();
            }
        });
    };

    handleABMapMove = (event) => {
        if (!this.props.selectingAB || !this.pendingABPoint || !this.abDrawingLayers) return;
        const latLngs = [this.pendingABPoint, event.latlng];
        if (!this.draftABLineLayers.length) {
            this.draftABLineLayers = this.createABLine(latLngs, this.abDrawingLayers);
            this.draftABEndMarker = this.createABMarker(event.latlng, 1, { interactive: false }).addTo(this.abDrawingLayers);
        } else {
            this.draftABLineLayers.forEach((layer) => layer.setLatLngs(latLngs));
            this.draftABEndMarker.setLatLng(event.latlng);
        }
    };

    handleABMapClick = (event) => {
        if (!this.props.selectingAB || this.props.editingDisabled || !this.abDrawingLayers) return;
        const point = event.latlng;
        if (!this.pendingABPoint) {
            this.pendingABPoint = point;
            this.createABMarker(point, 0, { interactive: false }).addTo(this.abDrawingLayers);
            return;
        }
        if (point.equals(this.pendingABPoint)) return;
        const first = this.pendingABPoint;
        this.clearABDrawing();
        this.props.onABChange([[first.lng, first.lat], [point.lng, point.lat]]);
    };

    fitMap = () => {
        if (!this.map || this.generatingFlightPath || this.draggingAB) return;
        let bounds = this.flightPathLayer && this.flightPathLayer.getBounds();
        if (!bounds || !bounds.isValid()) bounds = this.polygonLayer && this.polygonLayer.getBounds();
        if (bounds && bounds.isValid()) this.map.fitBounds(bounds, { padding: [24, 24], maxZoom: 17, animate: false });
    };

    setPassWidths = () => {
        if (!this.map) return;
        const metersPerPixel = 156543.03392 * Math.cos(this.map.getCenter().lat * Math.PI / 180) / Math.pow(2, this.map.getZoom());
        this.contextPassLayers.forEach(({ layer, swathWidthM }) => {
            if (Number(swathWidthM) > 0) layer.setStyle({ weight: Number(swathWidthM) / Math.max(metersPerPixel, 0.01) });
        });
        const swathWidthM = Number(this.props.swathWidthM);
        if (!(swathWidthM > 0)) return;
        const weight = swathWidthM / Math.max(metersPerPixel, 0.01);
        this.passLayers.forEach((layer) => layer.setStyle({ weight }));
        if (this.flightPathLayer) {
            this.flightPathLayer.setStyle({ weight: Math.min(3.5, weight * 0.25) });
        }
    };

    getPlanningPointLatLng = (point, polygonBounds) => {
        if (!point) return null;
        const lat = Number(point && point.lat);
        const lng = Number(point && point.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

        const directIsValid = Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
        const swappedIsValid = Math.abs(lng) <= 90 && Math.abs(lat) <= 180;
        if (!directIsValid && swappedIsValid) return L.latLng(lng, lat);
        if (!directIsValid || !swappedIsValid || !polygonBounds || !polygonBounds.isValid()) {
            return directIsValid ? L.latLng(lat, lng) : null;
        }

        const direct = L.latLng(lat, lng);
        const swapped = L.latLng(lng, lat);
        if (!polygonBounds.contains(direct) && polygonBounds.contains(swapped)) return swapped;

        const center = polygonBounds.getCenter();
        const directDistance = center.distanceTo(direct);
        const swappedDistance = center.distanceTo(swapped);
        if (directDistance > 50000 && swappedDistance < directDistance * 0.25) return swapped;
        return direct;
    };

    getStrategyPlan = () => {
        return getPolygonStrategyPlan(this.props.polygon, this.props.strategy, this.props.swathWidthM);
    };

    drawABPoints = (ab) => {
        if (!ab) return;
        const latLngs = ab.map((point) => [point[1], point[0]]);
        this.abLineLayers = this.createABLine(latLngs, this.layers);
        this.abMarkers = latLngs.map((point, index) => {
            const marker = this.createABMarker(point, index, {
                draggable: !this.props.selectingAB && !this.props.editingDisabled,
                autoPan: true,
                bubblingMouseEvents: true
            }).addTo(this.layers);
            marker.on('dragstart', () => { this.draggingAB = true; });
            marker.on('drag', () => {
                const points = this.abMarkers.map((item) => item.getLatLng());
                this.abLineLayers.forEach((layer) => layer.setLatLngs(points));
            });
            marker.on('dragend', () => {
                this.draggingAB = false;
                const points = this.abMarkers.map((item) => item.getLatLng());
                if (points[0].equals(points[1])) {
                    this.abMarkers.forEach((item, pointIndex) => item.setLatLng(latLngs[pointIndex]));
                    this.abLineLayers.forEach((layer) => layer.setLatLngs(latLngs));
                    return;
                }
                this.props.onABChange(points.map((item) => [item.lng, item.lat]));
            });
            return marker;
        });
        this.updateABEditingState();
    };

    drawPlanningPoints = (polygonBounds, ab) => {
        this.drawABPoints(ab);
        const runways = Array.isArray(this.props.runways) ? this.props.runways : [];
        const points = [
            { point: this.props.base, isBase: true, label: this.props.baseLabel },
            ...runways.map((point, index) => ({
                point,
                label: `${this.props.runwayLabel} ${index + 1}`
            }))
        ];
        points.forEach(({ point, isBase, label }) => {
            const latLng = this.getPlanningPointLatLng(point, polygonBounds);
            if (!latLng) return;
            L.marker(latLng, {
                title: label,
                alt: label,
                bubblingMouseEvents: true,
                icon: planningPointIcon(isBase)
            }).bindTooltip(label, {
                permanent: true,
                direction: 'top',
                offset: [0, -4],
                className: 'new-planning-polygon-label'
            }).addTo(this.layers);
        });
    };

    drawFlightPath = async () => {
        if (!this.map || !this.layers) return;
        const token = ++this.renderToken;
        const polygon = this.props.polygon;
        this.layers.clearLayers();
        this.flightPathLayer = null;
        this.polygonLayer = null;
        this.generatingFlightPath = false;
        this.abLineLayers = [];
        this.abMarkers = [];
        let polygonBounds = null;

        if (polygon && polygon.feature) {
            this.polygonLayer = L.geoJSON(polygon.feature, {
                pane: 'new-planning-flight-polygon',
                interactive: false,
                style: { color: '#ea580c', weight: 2, fillColor: '#fb923c', fillOpacity: 0.16 }
            });
            polygonBounds = this.polygonLayer.getBounds();
            this.polygonLayer.addTo(this.layers);
        }

        const { pattern, ab } = this.getStrategyPlan();
        const orderedLines = ab && pattern && pattern.orderedLines.length ? pattern.orderedLines : null;
        const passes = getFlightPasses(orderedLines ? { lines: orderedLines } : polygon && polygon.lines, Boolean(orderedLines));
        this.passLayers = passes.map((pass) => L.polyline(
            pass.coordinates.map((point) => [Number(point[1]), Number(point[0])]),
            { pane: 'new-planning-flight-application', interactive: false, color: '#16a34a', opacity: 1, weight: 5, lineCap: 'butt' }
        ).addTo(this.layers));
        if (!passes.length) {
            this.drawPlanningPoints(polygonBounds, ab);
            this.fitMap();
            return;
        }
        this.generatingFlightPath = true;
        try {
            const result = await generateFlightPath2D({
                passes,
                preservePassOrder: Boolean(orderedLines),
                startPoint: ab && ab[0],
                strategy: this.props.strategy,
                lineIndex: polygon.lineIndex,
                loopWidthM: polygon.loopWidthM,
                loopLengthM: polygon.loopLengthM,
                stripWidthM: this.props.swathWidthM
            });
            if (token !== this.renderToken || !this.map) return;
            if (result && Array.isArray(result.path) && result.path.length >= 2) {
                this.flightPathLayer = L.polyline(result.path, {
                    pane: 'new-planning-flight-path',
                    interactive: false,
                    color: '#fff',
                    opacity: 1,
                    weight: 3.5,
                    lineCap: 'round',
                    lineJoin: 'round'
                }).addTo(this.layers);
            }
            this.generatingFlightPath = false;
            this.drawPlanningPoints(polygonBounds, ab);
            this.fitMap();
            this.setPassWidths();
        } catch (_) {
            if (token === this.renderToken) {
                this.generatingFlightPath = false;
                this.drawPlanningPoints(polygonBounds, ab);
                this.fitMap();
                this.setPassWidths();
            }
        }
    };

    render() {
        return <div className={`new-planning-flight-map new-planning-map${this.props.selectingAB ? ' is-selecting-ab' : ''}`} ref={this.setMapElement} />;
    }
}

class NewPlanning extends React.Component {
    constructor(props) {
        super(props);
        const now = new Date();
        const locale = props.res && props.res.LINGUA ? props.res.LINGUA : 'pt-BR';
        const defaultTitle = `${(props.res && props.res.VOO) || 'VOO'} ${now.toLocaleDateString(locale)} ${now.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}`;
        this.state = {
            clients: [],
            aircraftBrands: [],
            aircraftModels: [],
            aircraftBrandId: '',
            aircraftModelId: '',
            aircraftConfiguration: {
                application_speed: '',
                travel_speed: '',
                balloon_speed: '',
                hopper_load: '',
                swath_width: '',
                balloon_width: '',
                balloon_length: ''
            },
            aircraftConfigurationEdited: {},
            loadingAircraftModels: false,
            loadingAircraftConfiguration: false,
            areas: [],
            importedAreas: [],
            areaSearch: '',
            clientId: '',
            selectedAreaIds: [],
            faixa: '',
            bulkFlow: '',
            polygonFlows: {},
            title: defaultTitle,
            autoMerge: true,
            activeModal: '',
            strategyMapPolygon: null,
            strategyMapInitialPolygon: null,
            strategyMapJobId: null,
            strategyMapDrafts: {},
            strategyMapClosing: false,
            strategyMapStrategy: '',
            strategyMapSelectingAB: false,
            strategyMapError: '',
            strategyMapEstimating: false,
            draftFaixa: '',
            base: null,
            runways: [],
            pointMode: '',
            areaSelectionMode: '',
            areaSelectionTool: 'rectangle',
            areaSelectionMenuAnchor: null,
            loadingOptions: true,
            loadingAreas: false,
            importing: false,
            generating: false,
            generationProgress: null,
            savingResultId: null,
            sending: false,
            downloading: false,
            estimate: null,
            job: null,
            jobs: [],
            error: ''
        };
        this.mapElement = null;
        this.map = null;
        this.mapLayers = null;
        this.areaLayers = null;
        this.labelLayers = null;
        this.clusterLayers = null;
        this.pointLayers = null;
        this.areaGeometries = [];
        this.visibleAreaGeometries = [];
        this.areaLayerById = new Map();
        this.areaGeometryById = new Map();
        this.areaVisibleIds = new Set();
        this.areaDisplayFrame = null;
        this.areaRenderFrame = null;
        this.areaRenderToken = 0;
        this.areaRequest = 0;
        this.areaSelectionDrawing = null;
        this.areaSelectionClickBlockedUntil = 0;
        this.aircraftModelRequest = 0;
        this.aircraftConfigurationRequest = 0;
        this.saveInProgress = false;
        this.generationController = null;
        this.generationDisposed = false;
        this.strategyEstimateTimer = null;
        this.strategyEstimateRequest = 0;
        this.strategyEstimateController = null;
        this.strategyEstimatePromise = null;
        this.strategyMapCloseInProgress = false;
        this.strategyMapContextJobs = null;
        this.strategyMapContext = [];
    }

    componentDidMount() {
        this.loadOptions();
    }

    componentDidUpdate(previousProps, previousState) {
        const preview = this.state.strategyMapPolygon;
        const previousPreview = previousState.strategyMapPolygon;
        if (!preview && previousPreview) this.cancelStrategyMapEstimate();
        if (preview && preview.previewEdited && !preview.estimateUnavailable && !preview.estimateOutputs && (!previousPreview ||
            preview.lines !== previousPreview.lines || preview.ab !== previousPreview.ab || preview.selectedAB !== previousPreview.selectedAB ||
            preview.selectedABBack !== previousPreview.selectedABBack || preview.swathWidthM !== previousPreview.swathWidthM)) {
            this.scheduleStrategyMapEstimate();
        }
        const mapModalOpen = this.state.activeModal === 'polygons';
        if (mapModalOpen && this.mapElement && !this.map) {
            this.initializeMap();
        } else if (!mapModalOpen && this.map) {
            this.stopAreaSelection(false);
            this.map.remove();
            this.map = null;
            this.mapLayers = null;
            if (this.areaDisplayFrame !== null) {
                window.cancelAnimationFrame(this.areaDisplayFrame);
                this.areaDisplayFrame = null;
            }
            if (this.areaRenderFrame !== null) {
                window.cancelAnimationFrame(this.areaRenderFrame);
                this.areaRenderFrame = null;
            }
            this.areaRenderToken += 1;
            this.areaLayers = null;
            this.labelLayers = null;
            this.clusterLayers = null;
            this.pointLayers = null;
            this.areaGeometries = [];
            this.visibleAreaGeometries = [];
            this.areaLayerById.clear();
            this.areaGeometryById.clear();
            this.areaVisibleIds.clear();
        }

        const areasChanged = previousState.areas !== this.state.areas ||
            previousState.importedAreas !== this.state.importedAreas ||
            previousState.clientId !== this.state.clientId;
        if (areasChanged) {
            this.stopAreaSelection();
            this.refreshMap(true);
            return;
        }

        if (previousState.selectedAreaIds !== this.state.selectedAreaIds) {
            this.updateSelectedAreaLayers(previousState.selectedAreaIds, this.state.selectedAreaIds);
        }

        if (previousState.base !== this.state.base || previousState.runways !== this.state.runways) {
            this.refreshMapPoints();
        }
    }

    componentWillUnmount() {
        this.generationDisposed = true;
        if (this.generationController) this.generationController.abort();
        this.cancelStrategyMapEstimate();
        this.stopAreaSelection(false);
        if (this.map) this.map.remove();
        this.map = null;
        if (this.areaDisplayFrame !== null) {
            window.cancelAnimationFrame(this.areaDisplayFrame);
            this.areaDisplayFrame = null;
        }
        if (this.areaRenderFrame !== null) {
            window.cancelAnimationFrame(this.areaRenderFrame);
            this.areaRenderFrame = null;
        }
        this.areaRenderToken += 1;
    }

    setMapElement = (element) => {
        this.mapElement = element;
        if (!element || this.state.activeModal !== 'polygons' || this.map) return;
        window.requestAnimationFrame(() => {
            if (this.mapElement === element && this.state.activeModal === 'polygons' && !this.map) {
                this.initializeMap();
            }
        });
    };

    text = (key, fallback) => this.props.res[key] || fallback;

    get = (path) => jsonRequest(`${Config.server}${path}`, { headers: tokenHeaders() });

    loadOptions = async () => {
        try {
            const [clients, aircraftBrands] = await Promise.all([
                this.get('/client'),
                this.get('/domain/AIRCRAFTBRAND')
            ]);
            this.setState({
                clients: Array.isArray(clients) ? clients : [],
                aircraftBrands: Array.isArray(aircraftBrands) ? aircraftBrands : [],
                loadingOptions: false
            }, () => this.loadAreas('0'));
        } catch (error) {
            this.setState({ loadingOptions: false, error: error.message });
        }
    };

    loadAreas = async (clientId) => {
        const requestId = ++this.areaRequest;
        const clientKey = String(clientId || '0');
        this.setState({ loadingAreas: true, areas: [], selectedAreaIds: [], areaSearch: '' });
        try {
            const areas = await this.get(`/areas/${clientKey}`);
            if (requestId !== this.areaRequest) return;
            const normalized = (Array.isArray(areas) ? areas : []).map((area) => Object.assign({}, area, {
                feature: areaToFeature(area)
            })).filter((area) => area.feature);
            this.setState({
                areas: normalized,
                loadingAreas: false
            });
        } catch (error) {
            if (requestId !== this.areaRequest) return;
            this.setState({ loadingAreas: false, error: error.message });
        }
    };

    initializeMap = () => {
        if (!this.mapElement) return;
        this.map = L.map(this.mapElement, { zoomControl: true, scrollWheelZoom: true }).setView([-15.78, -47.93], 4);
        this.map.attributionControl.setPrefix(false);
        L.tileLayer(Config.tile_google, Object.assign({ maxZoom: 21 }, Config.tile_google_config || {})).addTo(this.map);
        L.control.scale({ metric: true, imperial: false }).addTo(this.map);
        this.mapLayers = L.layerGroup().addTo(this.map);
        this.areaLayers = L.layerGroup().addTo(this.mapLayers);
        this.labelLayers = L.layerGroup().addTo(this.mapLayers);
        this.clusterLayers = L.layerGroup().addTo(this.mapLayers);
        this.pointLayers = L.layerGroup().addTo(this.mapLayers);
        this.map.on('moveend zoomend', this.requestAreaDisplayRefresh);
        this.map.on(L.Draw.Event.CREATED, this.completeAreaSelection);
        this.map.on('click', (event) => {
            if (!this.state.pointMode || this.state.areaSelectionMode || Date.now() < this.areaSelectionClickBlockedUntil) return;
            const point = { lat: event.latlng.lat, lng: event.latlng.lng };
            const key = this.state.pointMode;
            if (key === 'runway') {
                this.setState((state) => ({ runways: state.runways.concat(point), ...this.clearGenerated() }));
                return;
            }
            this.setState({ [key]: point, pointMode: '', ...this.clearGenerated() });
        });
        setTimeout(() => {
            if (!this.map) return;
            this.map.invalidateSize();
            this.refreshMap();
        }, 250);
    };

    requestAreaDisplayRefresh = () => {
        if (this.areaDisplayFrame !== null) return;
        this.areaDisplayFrame = window.requestAnimationFrame(() => {
            this.areaDisplayFrame = null;
            this.refreshAreaDisplay();
        });
    };

    stopAreaSelection = (updateState = true) => {
        const drawing = this.areaSelectionDrawing;
        this.areaSelectionDrawing = null;
        if (drawing) {
            drawing.off('disabled', this.handleAreaSelectionStopped);
            drawing.disable();
        }
        if (updateState && this.state.areaSelectionMode) this.setState({ areaSelectionMode: '' });
    };

    handleAreaSelectionStopped = () => {
        this.areaSelectionDrawing = null;
        this.areaSelectionClickBlockedUntil = Date.now() + 100;
        this.setState({ areaSelectionMode: '' });
    };

    startAreaSelection = (mode = this.state.areaSelectionTool) => {
        if (!this.map || this.props.singleAreaOnly) return;
        const nextMode = this.state.areaSelectionMode === mode ? '' : mode;
        this.stopAreaSelection(false);
        this.setState({ areaSelectionMode: nextMode, areaSelectionTool: mode, areaSelectionMenuAnchor: null, pointMode: '' }, () => {
            if (!nextMode || !this.map || this.state.activeModal !== 'polygons') return;
            const hint = nextMode === 'rectangle' ? this.props.res.SELECAO_RET_INSTRUCAO : this.props.res.SELECAO_LACO_INSTRUCAO;
            const options = {
                showArea: false,
                showLength: false,
                metric: true,
                shapeOptions: {
                    color: '#3388ff', weight: 3, opacity: 0.5, fill: false,
                    dashArray: '10, 10', dashOffset: '10', interactive: false
                }
            };
            const drawing = nextMode === 'rectangle' ? new L.Draw.Rectangle(this.map, options) : new L.Draw.Polygon(this.map, options);
            drawing._initialLabelText = hint;
            drawing._getTooltipText = () => ({ text: hint });
            this.areaSelectionDrawing = drawing;
            drawing.on('disabled', this.handleAreaSelectionStopped);
            drawing.enable();
        });
    };

    completeAreaSelection = (event) => {
        if (!this.areaSelectionDrawing || !this.state.areaSelectionMode || this.props.singleAreaOnly) return;
        const selector = event.layer.toGeoJSON();
        const bounds = event.layer.getBounds();
        const clusteredIds = new Set();
        const selectedClusterIds = new Set();
        this.clusterLayers.eachLayer((marker) => {
            if (!marker.areaIds || !marker.getLatLng) return;
            const center = marker.getLatLng();
            const inside = booleanPointInPolygon(point([center.lng, center.lat]), selector);
            marker.areaIds.forEach((id) => {
                clusteredIds.add(id);
                if (inside) selectedClusterIds.add(id);
            });
        });
        const selectedIds = this.areaGeometries.filter((geometry) => {
            const id = String(geometry.area.id);
            if (clusteredIds.has(id)) return selectedClusterIds.has(id);
            if (!bounds.intersects(geometry.areaBounds)) return false;
            const feature = geometry.area.feature;
            const polygons = feature.geometry.type === 'MultiPolygon'
                ? feature.geometry.coordinates.map((coordinates) => ({ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates } }))
                : [feature];
            return polygons.some((polygon) => booleanOverlap(polygon, selector) || booleanContains(selector, polygon));
        }).map((geometry) => String(geometry.area.id));
        this.areaSelectionClickBlockedUntil = Date.now() + 100;
        this.stopAreaSelection(false);
        this.setState({
            areaSelectionMode: '',
            selectedAreaIds: selectedIds,
            ...this.clearGenerated()
        });
    };

    toggleMapPointMode = (mode) => {
        this.stopAreaSelection(false);
        this.setState((state) => ({ pointMode: state.pointMode === mode ? '' : mode, areaSelectionMode: '' }));
    };

    refreshMap = (fitBounds = true) => {
        if (!this.map || !this.areaLayers) return;
        this.areaRenderToken += 1;
        if (this.areaRenderFrame !== null) {
            window.cancelAnimationFrame(this.areaRenderFrame);
            this.areaRenderFrame = null;
        }
        this.areaLayers.clearLayers();
        this.labelLayers.clearLayers();
        this.clusterLayers.clearLayers();
        this.pointLayers.clearLayers();
        this.areaLayerById.clear();
        this.areaGeometryById.clear();
        this.areaVisibleIds.clear();
        const selected = this.selectedAreas();
        const selectedIds = new Set(selected.map((area) => String(area.id)));
        const availableAreas = this.getAvailableAreas();
        const bounds = [];
        const geometries = availableAreas.map((area) => {
            if (!area.feature) return;
            const isSelected = selectedIds.has(String(area.id));
            const areaBounds = featureBounds(area.feature);
            if (!areaBounds || !areaBounds.isValid()) return null;
            if (!selected.length || isSelected) bounds.push(areaBounds);
            return {
                area,
                areaBounds,
                center: areaBounds.getCenter(),
                isSelected,
                label: area.name || `${this.text('TALHAO', 'Talhão')} ${area.id}`
            };
        });
        const validGeometries = geometries.filter(Boolean);
        this.areaGeometries = validGeometries;
        validGeometries.forEach((geometry) => {
            this.areaGeometryById.set(String(geometry.area.id), geometry);
        });

        const runways = Array.isArray(this.state.runways) ? this.state.runways : [];
        [this.state.base, ...runways].forEach((point) => {
            if (!point) return;
            bounds.push(L.latLngBounds([point.lat, point.lng], [point.lat, point.lng]));
        });
        this.refreshMapPoints();
        if (fitBounds && selected.length) {
            this.focusMapAreas();
        } else if (fitBounds && bounds.length) {
            const combined = bounds.reduce((result, current) => result.extend(current), L.latLngBounds([]));
            this.map.fitBounds(combined, { padding: [24, 24], maxZoom: 14 });
        } else if (fitBounds && (this.state.base || runways.length)) {
            const point = this.state.base || runways[0];
            this.map.setView([point.lat, point.lng], Math.max(this.map.getZoom(), 13));
        }
        this.requestAreaDisplayRefresh();
    };

    focusMapAreas = () => {
        if (!this.map) return;
        const selectedIds = new Set(this.state.selectedAreaIds.map(String));
        const bounds = L.latLngBounds([]);
        this.areaGeometries.forEach((geometry) => {
            if (!selectedIds.size || selectedIds.has(String(geometry.area.id))) {
                bounds.extend(geometry.areaBounds);
            }
        });
        if (bounds.isValid()) this.map.fitBounds(bounds, { padding: [24, 24], maxZoom: 17 });
    };

    areaLayerStyle = (isSelected) => isSelected
        ? { color: '#a16207', weight: 4, fillColor: '#facc15', fillOpacity: 0.42 }
        : { color: '#0369a1', weight: 3, fillColor: '#38bdf8', fillOpacity: 0.28 };

    updateSelectedAreaLayers = (previousIds, currentIds) => {
        const previous = new Set((previousIds || []).map(String));
        const current = new Set((currentIds || []).map(String));
        const changedIds = new Set([...previous, ...current]);
        changedIds.forEach((id) => {
            if (previous.has(id) === current.has(id)) return;
            const layer = this.areaLayerById.get(id);
            if (layer) layer.setStyle(this.areaLayerStyle(current.has(id)));
            const geometry = this.areaGeometryById.get(id);
            if (geometry) geometry.isSelected = current.has(id);
        });
        this.refreshAreaLabels();
    };

    createAreaLayer = (geometry) => L.geoJSON(geometry.area.feature, {
        style: this.areaLayerStyle(geometry.isSelected),
        onEachFeature: (_feature, featureLayer) => {
            featureLayer.on('click', () => {
                if (!this.state.pointMode && !this.state.areaSelectionMode && Date.now() >= this.areaSelectionClickBlockedUntil) this.toggleAreaSelection(geometry.area.id);
            });
        }
    });

    renderAreaBatch = (geometries, token, start = 0) => {
        if (token !== this.areaRenderToken || !this.map || !this.areaLayers) return;
        const end = Math.min(start + MEDIUM_RENDER_BATCH_SIZE, geometries.length);
        for (let index = start; index < end; index += 1) {
            const geometry = geometries[index];
            const id = String(geometry.area.id);
            let layer = this.areaLayerById.get(id);
            if (!layer) {
                layer = this.createAreaLayer(geometry);
                this.areaLayerById.set(id, layer);
            }
            if (!this.areaLayers.hasLayer(layer)) layer.addTo(this.areaLayers);
            this.areaVisibleIds.add(id);
        }
        if (end < geometries.length) {
            this.areaRenderFrame = window.requestAnimationFrame(() => {
                this.areaRenderFrame = null;
                this.renderAreaBatch(geometries, token, end);
            });
        }
    };

    refreshAreaDisplay = () => {
        if (!this.map || !this.areaLayers || !this.clusterLayers || !this.labelLayers) return;
        this.areaRenderToken += 1;
        const renderToken = this.areaRenderToken;
        if (this.areaRenderFrame !== null) {
            window.cancelAnimationFrame(this.areaRenderFrame);
            this.areaRenderFrame = null;
        }
        const zoom = this.map.getZoom();
        const mapBounds = this.map.getBounds();
        const viewport = mapBounds && mapBounds.isValid()
            ? mapBounds.pad(MEDIUM_VIEWPORT_PADDING)
            : null;
        const visibleGeometries = viewport
            ? this.areaGeometries.filter((geometry) => viewport.intersects(geometry.areaBounds))
            : this.areaGeometries;
        this.visibleAreaGeometries = visibleGeometries;
        const clusterMode = zoom < MEDIUM_RENDER_ZOOM || visibleGeometries.length > MEDIUM_RENDER_LIMIT;
        this.clusterLayers.clearLayers();
        if (!clusterMode) {
            const nextVisibleIds = new Set(visibleGeometries.map((geometry) => String(geometry.area.id)));
            Array.from(this.areaVisibleIds).forEach((id) => {
                if (nextVisibleIds.has(id)) return;
                const layer = this.areaLayerById.get(id);
                if (layer) this.areaLayers.removeLayer(layer);
                this.areaLayerById.delete(id);
                this.areaVisibleIds.delete(id);
            });
            const additions = visibleGeometries
                .filter((geometry) => !this.areaVisibleIds.has(String(geometry.area.id)))
                .sort((left, right) => Number(right.isSelected) - Number(left.isSelected));
            this.renderAreaBatch(additions, renderToken);
            this.refreshAreaLabels();
            return;
        }

        this.areaLayers.clearLayers();
        this.areaVisibleIds.clear();
        this.areaLayerById.clear();
        this.labelLayers.clearLayers();
        const gridSize = MEDIUM_CLUSTER_GRID_SIZE;
        const clusters = {};
        visibleGeometries.forEach((geometry) => {
            const projected = this.map.project(geometry.center, zoom);
            const key = `${Math.floor(projected.x / gridSize)}_${Math.floor(projected.y / gridSize)}`;
            if (!clusters[key]) {
                clusters[key] = {
                    count: 0,
                    latSum: 0,
                    lngSum: 0,
                    bounds: L.latLngBounds([]),
                    ids: []
                };
            }
            const cluster = clusters[key];
            cluster.count += 1;
            cluster.ids.push(String(geometry.area.id));
            cluster.latSum += geometry.center.lat;
            cluster.lngSum += geometry.center.lng;
            cluster.bounds.extend(geometry.areaBounds);
        });

        Object.keys(clusters).forEach((key) => {
            const cluster = clusters[key];
            const center = L.latLng(cluster.latSum / cluster.count, cluster.lngSum / cluster.count);
            const iconSize = cluster.count >= 100 ? 50 : 44;
            const countLabel = cluster.count === 1
                ? this.text('TALHAO', 'Talhão')
                : this.text('TALHOES_', 'Talhões');
            const marker = L.marker(center, {
                icon: L.divIcon({
                    className: 'new-planning-cluster-icon',
                    html: `<span>${cluster.count}</span>`,
                    iconSize: [iconSize, iconSize],
                    iconAnchor: [iconSize / 2, iconSize / 2]
                }),
                title: `${cluster.count} ${countLabel}`,
                bubblingMouseEvents: false
            });
            marker.areaIds = cluster.ids;
            marker.on('click', () => {
                if (!this.map || this.state.areaSelectionMode || Date.now() < this.areaSelectionClickBlockedUntil) return;
                if (cluster.count === 1) {
                    const nextZoom = Math.max(MEDIUM_RENDER_ZOOM, this.map.getZoom() + 2);
                    this.map.setView(center, Math.min(nextZoom, 20));
                } else if (cluster.bounds.isValid()) {
                    this.map.fitBounds(cluster.bounds.pad(0.15), { maxZoom: 20 });
                }
            });
            marker.addTo(this.clusterLayers);
        });
    };

    refreshAreaLabels = () => {
        if (!this.map || !this.labelLayers) return;
        this.labelLayers.clearLayers();
        if (this.map.getZoom() < MEDIUM_RENDER_ZOOM || this.visibleAreaGeometries.length > MEDIUM_RENDER_LIMIT) return;

        const labelCanvas = document.createElement('canvas');
        const labelContext = labelCanvas.getContext('2d');
        if (labelContext) labelContext.font = '700 12px Arial';
        const sortedGeometries = this.visibleAreaGeometries.slice().sort((left, right) => Number(right.isSelected) - Number(left.isSelected));
        const visibleLabels = new Set();
        const boxesByCell = new Map();
        const cellWidth = 64;
        const cellHeight = 28;
        const labelHeight = 20;
        const labelPadding = 3;

        sortedGeometries.forEach((geometry) => {
            const projected = this.map.project(geometry.center, this.map.getZoom());
            const measuredWidth = labelContext ? labelContext.measureText(geometry.label).width : geometry.label.length * 7;
            const labelWidth = Math.min(260, Math.max(40, measuredWidth + 16));
            const box = {
                left: projected.x - labelWidth / 2 - labelPadding,
                right: projected.x + labelWidth / 2 + labelPadding,
                top: projected.y - labelHeight / 2 - labelPadding,
                bottom: projected.y + labelHeight / 2 + labelPadding
            };
            const minX = Math.floor(box.left / cellWidth);
            const maxX = Math.floor(box.right / cellWidth);
            const minY = Math.floor(box.top / cellHeight);
            const maxY = Math.floor(box.bottom / cellHeight);
            const cells = [];
            const checkedBoxes = new Set();
            let hasCollision = false;

            for (let x = minX; x <= maxX && !hasCollision; x++) {
                for (let y = minY; y <= maxY && !hasCollision; y++) {
                    const key = `${x}_${y}`;
                    cells.push(key);
                    const nearbyBoxes = boxesByCell.get(key) || [];
                    nearbyBoxes.forEach((nearbyBox) => {
                        if (checkedBoxes.has(nearbyBox)) return;
                        checkedBoxes.add(nearbyBox);
                        if (box.left < nearbyBox.right && box.right > nearbyBox.left &&
                            box.top < nearbyBox.bottom && box.bottom > nearbyBox.top) hasCollision = true;
                    });
                }
            }
            if (hasCollision) return;

            visibleLabels.add(geometry);
            cells.forEach((key) => {
                const cellBoxes = boxesByCell.get(key);
                if (cellBoxes) cellBoxes.push(box);
                else boxesByCell.set(key, [box]);
            });
        });

        this.visibleAreaGeometries.forEach((geometry) => {
            if (!visibleLabels.has(geometry)) return;
            const label = document.createElement('span');
            label.textContent = geometry.label;
            L.tooltip({ permanent: true, direction: 'center', className: 'new-planning-polygon-label', interactive: false })
                .setLatLng(geometry.center)
                .setContent(label)
                .addTo(this.labelLayers);
        });
    };

    refreshMapPoints = () => {
        if (!this.pointLayers) return;
        this.pointLayers.clearLayers();
        const runways = Array.isArray(this.state.runways) ? this.state.runways : [];
        [
            { point: this.state.base, isBase: true, label: this.text('BASE', 'Base') },
            ...runways.map((point, index) => ({
                point,
                label: `${this.text('PISTA', 'Pista')} ${index + 1}`
            }))
        ].forEach(({ point, isBase, label }) => {
            if (!point) return;
            L.marker([point.lat, point.lng], {
                title: label,
                alt: label,
                bubblingMouseEvents: true,
                icon: planningPointIcon(isBase)
            }).bindTooltip(label, {
                permanent: true, direction: 'top', offset: [0, -4], className: 'new-planning-polygon-label'
            }).addTo(this.pointLayers);
        });
    };

    selectedPlane = () => {
        const model = this.state.aircraftModels.find((item) => String(item.id) === String(this.state.aircraftModelId));
        const brand = this.state.aircraftBrands.find((item) => String(item.id) === String(this.state.aircraftBrandId));
        const configuration = this.state.aircraftConfiguration;
        const canonicalValue = (field, convert) => {
            const rawValue = configuration[field];
            if (rawValue === '' || rawValue === null || rawValue === undefined) return null;
            const value = Number(String(rawValue).replace(',', '.'));
            return Number.isFinite(value) ? convert(value) : null;
        };
        const swathWidth = canonicalValue('swath_width', (value) => Units.toCanonicalLength(value, this.props.res));
        if (!(Number(swathWidth) > 0)) return null;
        return {
            name: [brand && brand.name, model && model.name].filter(Boolean).join(' '),
            id_aircraftmodel: model ? model.id : null,
            application_speed: canonicalValue('application_speed', (value) => Units.toCanonicalSpeed(value, this.props.res)),
            travel_speed: canonicalValue('travel_speed', (value) => Units.toCanonicalSpeed(value, this.props.res)),
            balloon_speed: canonicalValue('balloon_speed', (value) => Units.toCanonicalSpeed(value, this.props.res)),
            hopper_load: canonicalValue('hopper_load', (value) => Units.toCanonicalVolume(value, this.props.res, 'L')),
            swath_width: swathWidth,
            balloon_width: canonicalValue('balloon_width', (value) => Units.toCanonicalLength(value, this.props.res)),
            balloon_length: canonicalValue('balloon_length', (value) => Units.toCanonicalLength(value, this.props.res))
        };
    };

    selectedClient = () => this.state.clients.find((client) => String(client.id) === String(this.state.clientId)) || null;

    getAvailableAreas = () => this.state.clientId === IMPORTED_CLIENT_ID
        ? this.state.importedAreas
        : this.state.areas;

    selectedPlanes = () => {
        const plane = this.selectedPlane();
        return plane ? [plane] : [];
    };

    selectedAreas = () => {
        const ids = this.state.selectedAreaIds.map(String);
        return this.getAvailableAreas().filter((area) => ids.indexOf(String(area.id)) >= 0);
    };

    renderAreaSelectionRow = (area) => {
        const id = String(area.id);
        const selected = this.state.selectedAreaIds.indexOf(id) >= 0;
        const type = String(area.type || 'N').toUpperCase();
        const zoneType = type === 'X' || type === 'XL' ? 'X' : type === 'T' || type === 'R' ? type : null;
        const selectedPlane = this.selectedPlane();
        const defaultFlow = this.state.bulkFlow || (selectedPlane && selectedPlane.flow_rate ? String(selectedPlane.flow_rate) : '10');
        return (
            <PlanningAreaSelectionRow
                key={id}
                area={area}
                id={id}
                selected={selected}
                flow={this.state.polygonFlows[id]}
                defaultFlow={defaultFlow}
                singleAreaOnly={this.props.singleAreaOnly}
                areaLabel={this.text('TALHAO', 'Talhão')}
                flowLabel={this.props.res.FLUXO}
                zoneType={zoneType}
                zoneLabel={zoneType ? this.props.res[`POLY_${zoneType}`] : undefined}
                onSelectionChange={this.handleAreaSelectionChange}
                onFlowChange={this.handlePolygonFlowChange}
            />
        );
    };

    clearGenerated = () => ({ estimate: null, job: null, jobs: [], error: '', generationProgress: null });

    handleClientChange = (event) => {
        const clientId = String(event.target.value);
        if (clientId === IMPORTED_CLIENT_ID) {
            this.areaRequest += 1;
            const importedAreas = this.state.importedAreas;
            const importedAreaIds = importedAreas.map((area) => String(area.id));
            this.setState({
                clientId,
                loadingAreas: false,
                selectedAreaIds: this.props.singleAreaOnly ? importedAreaIds.slice(0, 1) : importedAreaIds,
                polygonFlows: {},
                areaSearch: '',
                base: null,
                runways: [],
                pointMode: '',
                ...this.clearGenerated()
            });
            return;
        }
        this.setState({
            clientId,
            selectedAreaIds: [],
            polygonFlows: {},
            base: null,
            runways: [],
            pointMode: '',
            ...this.clearGenerated()
        }, () => this.loadAreas(clientId || '0'));
    };

    configurationWithoutManualChanges = (configuration, edited) => Object.keys(configuration).reduce((result, field) => {
        result[field] = edited[field] ? configuration[field] : '';
        return result;
    }, {});

    aircraftConfigurationFields = () => {
        const { res } = this.props;
        return [
            { id: 'swath_width', label: Tools.replaceString(res._LARGURA_FAIXA, Units.getLengthUnit(res)) },
            { id: 'application_speed', label: Tools.replaceString(res._VELOCIDADE_APLICACAO, Units.getSpeedUnit(res)) },
            { id: 'travel_speed', label: Tools.replaceString(res._VELOCIDADE_TRANSLADO, Units.getSpeedUnit(res)) },
            { id: 'balloon_speed', label: Tools.replaceString(res._VELOCIDADE_MEDIA_BALAO, Units.getSpeedUnit(res)) },
            { id: 'hopper_load', label: Tools.replaceString(res._CARGO_HOPPER, Units.getVolumeUnit(res, 'L')) },
            { id: 'balloon_width', label: Tools.replaceString(res._TAMANHO_BALAO, Units.getLengthUnit(res)) },
            { id: 'balloon_length', label: Tools.replaceString(res._DISTANCIA_BALAO, Units.getLengthUnit(res)) }
        ];
    };

    handleAircraftBrandChange = (event) => {
        const aircraftBrandId = String(event.target.value);
        this.aircraftModelRequest += 1;
        this.aircraftConfigurationRequest += 1;
        this.setState((state) => ({
            aircraftBrandId,
            aircraftModels: [],
            aircraftModelId: '',
            loadingAircraftModels: Boolean(aircraftBrandId),
            loadingAircraftConfiguration: false,
            aircraftConfiguration: this.configurationWithoutManualChanges(state.aircraftConfiguration, state.aircraftConfigurationEdited),
            ...this.clearGenerated()
        }), () => {
            if (aircraftBrandId) this.loadAircraftModels(aircraftBrandId);
        });
    };

    loadAircraftModels = async (brandId) => {
        const requestId = ++this.aircraftModelRequest;
        try {
            const models = await this.get(`/domain/AIRCRAFTMODEL/${brandId}`);
            if (requestId !== this.aircraftModelRequest || String(this.state.aircraftBrandId) !== String(brandId)) return;
            this.setState({ aircraftModels: Array.isArray(models) ? models : [], loadingAircraftModels: false });
        } catch (error) {
            if (requestId !== this.aircraftModelRequest) return;
            this.setState({ loadingAircraftModels: false, error: error.message });
        }
    };

    handleAircraftModelChange = (event) => {
        const aircraftModelId = String(event.target.value);
        this.aircraftConfigurationRequest += 1;
        this.setState((state) => ({
            aircraftModelId,
            loadingAircraftConfiguration: Boolean(aircraftModelId),
            aircraftConfiguration: this.configurationWithoutManualChanges(state.aircraftConfiguration, state.aircraftConfigurationEdited),
            ...this.clearGenerated()
        }), () => this.loadAircraftConfiguration(aircraftModelId));
    };

    configurationValueForDisplay = (field, value) => {
        const number = Number(String(value).replace(',', '.'));
        if (!Number.isFinite(number)) return '';
        const { res } = this.props;
        if (field === 'application_speed' || field === 'travel_speed' || field === 'balloon_speed') {
            return String(Number(Units.convertSpeed(number, 'km/h', res).toFixed(2)));
        }
        if (field === 'hopper_load') {
            return String(Number(Units.convertVolume(number, 'L', res).toFixed(2)));
        }
        return String(Number(Units.convertLength(number, 'm', res).toFixed(2)));
    };

    loadAircraftConfiguration = async (modelId) => {
        if (!modelId) {
            this.aircraftConfigurationRequest += 1;
            this.setState({ loadingAircraftConfiguration: false });
            return;
        }
        const requestId = ++this.aircraftConfigurationRequest;
        try {
            const configurations = await this.get(`/plane_model_configuration/${modelId}`);
            if (requestId !== this.aircraftConfigurationRequest || String(this.state.aircraftModelId) !== String(modelId)) return;
            const configurationRow = Array.isArray(configurations) ? configurations[0] : configurations;
            const aircraftConfiguration = Object.assign({}, this.state.aircraftConfiguration);
            Object.keys(aircraftConfiguration).forEach((field) => {
                if (this.state.aircraftConfigurationEdited[field]) return;
                const value = configurationRow && configurationRow[field];
                const numericValue = Number(String(value == null ? '' : value).replace(',', '.'));
                if (field === 'swath_width' && !(numericValue > 0)) {
                    aircraftConfiguration[field] = this.state.activeModal === 'aircraft' ? '' : defaultSwathWidth(this.props.res);
                } else {
                    aircraftConfiguration[field] = value === null || value === undefined
                        ? ''
                        : this.configurationValueForDisplay(field, value);
                }
            });
            this.setState({ aircraftConfiguration, loadingAircraftConfiguration: false, error: '' });
        } catch (error) {
            if (requestId !== this.aircraftConfigurationRequest) return;
            this.setState({ loadingAircraftConfiguration: false, error: error.message });
        }
    };

    handleAircraftConfigurationChange = (field) => (event) => {
        const value = event.target.value;
        this.setState((state) => ({
            aircraftConfiguration: Object.assign({}, state.aircraftConfiguration, { [field]: value }),
            aircraftConfigurationEdited: Object.assign({}, state.aircraftConfigurationEdited, { [field]: true }),
            ...this.clearGenerated()
        }));
    };

    handleFileImport = async (event) => {
        const input = event.target;
        const files = Array.from(input.files || []);
        if (!files.length) return;
        this.setState({ importing: true, error: '' });
        try {
            const shapeFiles = files.filter((file) => fileExtension(file) === 'shp');
            const importFiles = shapeFiles.length
                ? shapeFiles
                : files.filter((file) => ['geojson', 'json', 'kml', 'kmz', 'zip'].indexOf(fileExtension(file)) >= 0);
            const parsedFiles = await Promise.all(importFiles.map(async (file) => ({
                file,
                features: await polygonsFromFile(file, files)
            })));
            const startId = Date.now();
            const importedAreas = parsedFiles.reduce((areas, parsed) => areas.concat(parsed.features.map((feature, index) => ({
                id: `imported-${startId}-${areas.length + index}`,
                name: featureLabel(feature, parsed.file.name, index),
                feature,
                source: 'file'
            }))), []);
            if (!importedAreas.length) throw new Error(this.text('ERRO', 'Erro'));
            const allImportedAreas = this.state.importedAreas.concat(importedAreas);
            const importedAreaIds = allImportedAreas.map((area) => String(area.id));
            this.areaRequest += 1;
            this.setState({
                importedAreas: allImportedAreas,
                clientId: IMPORTED_CLIENT_ID,
                loadingAreas: false,
                selectedAreaIds: this.props.singleAreaOnly
                    ? importedAreaIds.slice(0, 1)
                    : importedAreaIds,
                polygonFlows: {},
                areaSearch: '',
                base: null,
                runways: [],
                pointMode: '',
                importing: false,
                ...this.clearGenerated()
            });
        } catch (_) {
            this.setState({ importing: false, error: this.text('ERRO', 'Erro') });
        } finally {
            input.value = '';
        }
    };

    handleField = (field) => (event) => this.setState({ [field]: event.target.value, ...this.clearGenerated() });

    openModal = (activeModal) => {
        const defaultFlow = '10';
        this.setState({
            activeModal,
            draftFaixa: this.state.faixa,
            ...(activeModal === 'polygons' && !this.state.bulkFlow ? { bulkFlow: defaultFlow } : {})
        });
    };

    closeModal = () => this.setState((state) => ({
        activeModal: '',
        pointMode: '',
        areaSelectionMode: '',
        areaSelectionMenuAnchor: null,
        ...(state.activeModal === 'aircraft'
            && !(Number(String(state.aircraftConfiguration.swath_width || '').replace(',', '.')) > 0)
            ? { aircraftConfiguration: Object.assign({}, state.aircraftConfiguration, { swath_width: defaultSwathWidth(this.props.res) }) }
            : {})
    }));

    saveFaixa = () => this.setState({
        faixa: this.state.draftFaixa,
        activeModal: '',
        ...this.clearGenerated()
    });

    renderChoiceButton = (modal, icon, title, value) => (
        <Button className="new-planning-choice" fullWidth onClick={() => this.openModal(modal)}>
            <MaterialIcon icon={icon} className="new-planning-choice-icon" />
            <span className="new-planning-choice-copy">
                <span className="new-planning-choice-title">{title}</span>
                <strong>{value}</strong>
            </span>
            <MaterialIcon icon="chevron_right" className="new-planning-choice-arrow" />
        </Button>
    );

    renderModalMap = () => (
        <div className={`new-planning-modal-map-wrap${this.state.pointMode || this.state.areaSelectionMode ? ' is-placing-point' : ''}${this.state.areaSelectionMode ? ' is-selecting-areas' : ''}`}>
            <Button
                className="new-planning-map-focus"
                size="small"
                variant="outlined"
                color="primary"
                aria-label={this.props.res.FOCAR_TALHOES}
                title={this.props.res.FOCAR_TALHOES}
                disabled={this.state.loadingAreas || !this.getAvailableAreas().length}
                onClick={this.focusMapAreas}
            >
                <MaterialIcon icon="center_focus_strong" aria-hidden="true" />
            </Button>
            <div className="new-planning-map-toolbar">
                <Button
                    size="small"
                    variant={this.state.pointMode === 'base' ? 'contained' : 'outlined'}
                    startIcon={<img src={baseIcon} className="new-planning-point-tool-icon" alt="" aria-hidden="true" />}
                    color="primary"
                    aria-pressed={this.state.pointMode === 'base'}
                    onClick={() => this.toggleMapPointMode('base')}
                >
                    {this.text('BASE', 'Base')}{this.state.base ? ' ✓' : ''}
                </Button>
                <Button
                    size="small"
                    variant={this.state.pointMode === 'runway' ? 'contained' : 'outlined'}
                    startIcon={<img src={runwayIcon} className="new-planning-point-tool-icon" alt="" aria-hidden="true" />}
                    color="primary"
                    aria-pressed={this.state.pointMode === 'runway'}
                    onClick={() => this.toggleMapPointMode('runway')}
                >
                    {this.text('PISTA', 'Pista')}{this.state.runways.length ? ` (${this.state.runways.length})` : ''}
                </Button>
                {this.state.runways.length > 0 && (
                    <Button
                        size="small"
                        onClick={() => this.setState({ runways: [], pointMode: '', ...this.clearGenerated() })}
                    >
                        {this.text('LIMPAR_PISTAS', 'Limpar pistas')}
                    </Button>
                )}
                {this.state.pointMode && (
                    <Typography className="new-planning-map-toolbar-hint" variant="caption">
                        {this.state.pointMode === 'base'
                            ? this.text('CLIQUE_PARA_BASE', 'Clique no mapa para definir a base.')
                            : this.text('CLIQUE_PARA_ADICIONAR_PISTA', 'Clique no mapa para adicionar uma pista.')}
                    </Typography>
                )}
            </div>
            {!this.props.singleAreaOnly && (
                <div className="new-planning-map-selection-toolbar">
                    <div className="new-planning-map-selection-buttons">
                        <Button
                            size="small"
                            color="primary"
                            variant={this.state.areaSelectionMode ? 'contained' : 'outlined'}
                            aria-pressed={Boolean(this.state.areaSelectionMode)}
                            aria-label={this.state.areaSelectionTool === 'rectangle' ? this.props.res.SELECAO_RET : this.props.res.SELECAO_LIVRE}
                            title={this.state.areaSelectionTool === 'rectangle' ? this.props.res.SELECAO_RET : this.props.res.SELECAO_LIVRE}
                            disabled={this.state.loadingAreas || !this.getAvailableAreas().length}
                            onClick={() => this.startAreaSelection()}
                        >
                            <SvgIcon viewBox="74 16 28 26" aria-hidden="true"><path d={AREA_SELECTION_ICONS[this.state.areaSelectionTool]} /></SvgIcon>
                        </Button>
                        <Button
                            size="small"
                            color="primary"
                            variant="outlined"
                            aria-label={this.props.res.SELECIONAR}
                            aria-haspopup="menu"
                            aria-expanded={Boolean(this.state.areaSelectionMenuAnchor)}
                            disabled={this.state.loadingAreas || !this.getAvailableAreas().length}
                            onClick={(event) => this.setState({ areaSelectionMenuAnchor: event.currentTarget })}
                        >
                            <MaterialIcon icon="arrow_drop_down" aria-hidden="true" />
                        </Button>
                    </div>
                    <Menu
                        anchorEl={this.state.areaSelectionMenuAnchor}
                        open={Boolean(this.state.areaSelectionMenuAnchor)}
                        onClose={() => this.setState({ areaSelectionMenuAnchor: null })}
                    >
                        {[
                            { mode: 'rectangle', label: this.props.res.SELECAO_RET },
                            { mode: 'lasso', label: this.props.res.SELECAO_LIVRE }
                        ].map(({ mode, label }) => (
                            <MenuItem key={mode} selected={this.state.areaSelectionTool === mode} onClick={() => this.startAreaSelection(mode)}>
                                <SvgIcon viewBox="74 16 28 26" style={{ marginRight: 8 }} aria-hidden="true"><path d={AREA_SELECTION_ICONS[mode]} /></SvgIcon>
                                {label}
                            </MenuItem>
                        ))}
                    </Menu>
                    {this.state.areaSelectionMode && (
                        <Typography className="new-planning-map-toolbar-hint" variant="caption">
                            {this.state.areaSelectionMode === 'rectangle' ? this.props.res.SELECAO_RET_INSTRUCAO : this.props.res.SELECAO_LACO_INSTRUCAO}
                        </Typography>
                    )}
                </div>
            )}
            <div ref={this.setMapElement} className="new-planning-map" />
        </div>
    );

    renderSelectionDialog = () => {
        const { res } = this.props;
        const modal = this.state.activeModal;
        const availableAreas = this.getAvailableAreas();
        const selectedAreaIds = new Set(this.state.selectedAreaIds.map(String));
        const selectedAreas = availableAreas.filter((area) => selectedAreaIds.has(String(area.id)));
        const areaQuery = normalizeSearchText(this.state.areaSearch.trim());
        const filteredAreas = areaQuery
            ? availableAreas.filter((area) => normalizeSearchText(
                area.name || `${this.text('TALHAO', 'Talhão')} ${area.id}`
            ).includes(areaQuery))
            : availableAreas;
        const unselectedAreas = filteredAreas.filter((area) => !selectedAreaIds.has(String(area.id)));
        if (!modal) return null;

        let title = '';
        let content = null;
        let actions = <Button color="primary" onClick={this.closeModal}>{this.text('CONCLUIR', 'Concluir')}</Button>;

        if (modal === 'aircraft') {
            title = `${res.AERONAVE} / ${res.CONFIGURACOES}`;
            content = (
                <>
                    <div className="new-planning-aircraft-selection">
                        <FormControl variant="outlined" fullWidth margin="normal">
                            <InputLabel>{res.FABRICANTE}</InputLabel>
                            <Select value={this.state.aircraftBrandId} onChange={this.handleAircraftBrandChange} label={res.FABRICANTE}>
                                <MenuItem value=""><em>&nbsp;</em></MenuItem>
                                {this.state.aircraftBrands.map((brand) => <MenuItem key={brand.id} value={String(brand.id)}>{brand.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl variant="outlined" fullWidth margin="normal" disabled={!this.state.aircraftBrandId || this.state.loadingAircraftModels}>
                            <InputLabel>{res.MODELO}</InputLabel>
                            <Select value={this.state.aircraftModelId} onChange={this.handleAircraftModelChange} label={res.MODELO}>
                                <MenuItem value=""><em>&nbsp;</em></MenuItem>
                                {this.state.aircraftModels.map((model) => <MenuItem key={model.id} value={String(model.id)}>{model.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </div>
                    <Typography variant="subtitle2" className="new-planning-aircraft-configuration-title">
                        {res.CONFIGURACOES}
                        {this.state.loadingAircraftConfiguration && <CircularProgress size={16} />}
                    </Typography>
                    <div className="new-planning-aircraft-configuration">
                        {this.aircraftConfigurationFields().map((field) => (
                            <TextField
                                key={field.id}
                                type="number"
                                variant="outlined"
                                label={field.label}
                                value={field.id === 'swath_width' && !this.state.aircraftConfiguration[field.id]
                                    ? defaultSwathWidth(res)
                                    : this.state.aircraftConfiguration[field.id]}
                                onChange={this.handleAircraftConfigurationChange(field.id)}
                                inputProps={{ step: 0.01 }}
                            />
                        ))}
                    </div>
                </>
            );
            actions = <Button onClick={this.closeModal}>{this.text('FECHAR', 'Fechar')}</Button>;
        } else if (modal === 'client') {
            title = this.text('CLIENTE', 'Cliente');
            content = (
                <FormControl variant="outlined" fullWidth margin="normal">
                    <InputLabel>{res.CLIENTE}</InputLabel>
                    <Select value={this.state.clientId} onChange={this.handleClientChange} label={res.CLIENTE}>
                        <MenuItem value=""><em>{this.text('SEM_CLIENTE', 'Sem cliente')}</em></MenuItem>
                        {this.state.importedAreas.length > 0 && <MenuItem value={IMPORTED_CLIENT_ID}><em>{this.text('IMPORTADO', 'Importado')}</em></MenuItem>}
                        {this.state.clients.map((client) => <MenuItem key={client.id} value={String(client.id)}>{client.name}</MenuItem>)}
                    </Select>
                </FormControl>
            );
            actions = <Button onClick={this.closeModal}>{this.text('FECHAR', 'Fechar')}</Button>;
        } else if (modal === 'polygons') {
            title = `${res.CLIENTE} / ${this.text('TALHOES_', 'Talhões')}`;
            content = (
                <div className="new-planning-modal-layout">
                    <div className="new-planning-modal-list">
                        <div className="new-planning-modal-controls">
                            <FormControl variant="outlined" fullWidth margin="normal">
                                <InputLabel>{res.CLIENTE}</InputLabel>
                                <Select value={this.state.clientId} onChange={this.handleClientChange} label={res.CLIENTE}>
                                    <MenuItem value=""><em>{this.text('SEM_VINCULO', res.SEM_CLIENTE)}</em></MenuItem>
                                    {this.state.importedAreas.length > 0 && <MenuItem value={IMPORTED_CLIENT_ID}><em>{this.text('IMPORTADO', 'Importado')}</em></MenuItem>}
                                    {this.state.clients.map((client) => <MenuItem key={client.id} value={String(client.id)}>{client.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <div className="new-planning-aircraft-bulk">
                                <TextField
                                    fullWidth
                                    type="number"
                                    variant="outlined"
                                    label={res.FLUXO}
                                    value={this.state.bulkFlow}
                                    onChange={(event) => {
                                        const bulkFlow = event.target.value;
                                        const polygonFlows = Object.assign({}, this.state.polygonFlows);
                                        this.selectedAreas().filter(isApplicationArea).forEach((area) => { polygonFlows[String(area.id)] = bulkFlow; });
                                        this.setState({ bulkFlow, polygonFlows, ...this.clearGenerated() });
                                    }}
                                    inputProps={{ min: 0.1, step: 0.1 }}
                                />
                            </div>
                            <Button component="label" color="primary" variant="outlined" disabled={this.state.importing}>
                                {this.state.importing ? <CircularProgress size={18} /> : this.text('IMPORTAR', 'Importar')}
                                <input type="file" hidden multiple accept=".geojson,.json,.kml,.kmz,.zip,.shp,.dbf,.prj,.shx,.cpg" onChange={this.handleFileImport} />
                            </Button>
                            <TextField
                                className="new-planning-search"
                                fullWidth
                                size="small"
                                variant="outlined"
                                placeholder={this.text('BUSCAR_TALHAO', 'Buscar talhão pelo nome')}
                                value={this.state.areaSearch}
                                onChange={(event) => this.setState({ areaSearch: event.target.value })}
                                inputProps={{ 'aria-label': this.text('BUSCAR_TALHAO', 'Buscar talhão pelo nome') }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                                }}
                            />
                        </div>
                        <div className="new-planning-area-lists">
                            {!this.state.loadingAreas && selectedAreas.length > 0 && (
                                <div className="new-planning-selected-area-list">
                                    {selectedAreas.map(this.renderAreaSelectionRow)}
                                </div>
                            )}
                            <div className="new-planning-area-list">
                                {this.state.loadingAreas && <CircularProgress size={22} />}
                                {!this.state.loadingAreas && availableAreas.length === 0 && (
                                    <Typography variant="body2" color="textSecondary">{this.text('NENHUM_TALHAO', 'Nenhum talhão disponível para este cliente.')}</Typography>
                                )}
                                {!this.state.loadingAreas && availableAreas.length > 0 && filteredAreas.length === 0 && (
                                    <Typography variant="body2" color="textSecondary">{this.text('NENHUM_TALHAO_ENCONTRADO', 'Nenhum talhão encontrado.')}</Typography>
                                )}
                                {!this.state.loadingAreas && unselectedAreas.map(this.renderAreaSelectionRow)}
                            </div>
                        </div>
                    </div>
                    {this.renderModalMap()}
                </div>
            );
        } else if (modal === 'swath') {
            title = this.text('FAIXA', 'Faixa');
            content = (
                <TextField
                    autoFocus
                    fullWidth
                    type="number"
                    variant="outlined"
                    label={res.FAIXA}
                    value={this.state.draftFaixa}
                    onChange={(event) => this.setState({ draftFaixa: event.target.value })}
                    inputProps={{ min: 0.1, step: 0.1 }}
                />
            );
            actions = (
                <>
                    <Button onClick={this.closeModal}>{this.text('CANCELAR', 'Cancelar')}</Button>
                    <Button color="primary" variant="contained" onClick={this.saveFaixa}>{this.text('SALVAR', 'Salvar')}</Button>
                </>
            );
        } else if (modal === 'options') {
            title = this.text('OPCOES_PLANEJAMENTO', 'Opções do planejamento');
            content = (
                <>
                    <TextField
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        label={this.text('NOME_PLANEJAMENTO', 'Nome do planejamento')}
                        value={this.state.title}
                        onChange={this.handleField('title')}
                        inputProps={{ maxLength: 99 }}
                    />
                </>
            );
        }

        return (
            <Dialog
                open
                fullWidth
                maxWidth={modal === 'polygons' ? 'lg' : modal === 'aircraft' ? 'md' : 'sm'}
                PaperProps={{ className: 'new-planning-dialog-paper' }}
                onClose={this.closeModal}
                aria-labelledby="new-planning-dialog-title"
            >
                <DialogTitle id="new-planning-dialog-title">{title}</DialogTitle>
                <DialogContent>{content}</DialogContent>
                <DialogActions>{actions}</DialogActions>
            </Dialog>
        );
    };

    renderStrategyMapOption = (strategy) => (
        <span className="new-planning-strategy-option">
            <span className="new-planning-strategy-option-name" title={this.text(strategy.labelKey, strategy.type)}>
                {this.text(strategy.labelKey, strategy.type)}
            </span>
            <span className="new-planning-strategy-option-time">
                {this.state.strategyMapEstimating
                    ? <CircularProgress size={16} aria-label={this.props.res.CALCULANDO_TEMPOS_ESTRATEGIAS} />
                    : Number.isFinite(strategy.seconds) ? formatDuration(strategy.seconds) : ''}
            </span>
        </span>
    );

    renderStrategyMapSummary = (polygon) => {
        const { res } = this.props;
        const candidate = polygon.lines;
        const count = candidate && Array.isArray(candidate.lines) ? candidate.lines.length : polygon.lineCount;
        const area = Number.isFinite(polygon.areaHectares) ? polygon.areaHectares : featureAreaHectares(polygon.feature);
        const abs = candidate && candidate.ABs;
        const reference = abs && (polygon.selectedABBack === 1
            ? (abs.mirroredAB || abs.endAB)
            : (abs.initialAB || abs.startAB));
        const ab = reference && Array.isArray(reference.c)
            ? (polygon.selectedAB === 1 ? reference.c : reference.c.slice().reverse())
            : polygon.ab;
        const values = [];
        const labels = [];
        if (Number.isFinite(area)) {
            const areaText = `${Units.formatValue(Units.convertArea(area, 'ha', res), res, 1)} ${Units.getAreaUnit(res)}`;
            values.push(areaText);
            labels.push(`${res.AREA}: ${areaText}`);
        }
        if (Number.isFinite(count)) {
            const countText = `${count} ${count === 1 ? res.FAIXA_UNIDADE : res.FAIXAS}`;
            values.push(countText);
            labels.push(countText);
        }
        if (Array.isArray(ab) && ab.length >= 2 && ab.slice(0, 2).every((coordinate) =>
            Array.isArray(coordinate) && coordinate.length >= 2 &&
            coordinate[0] !== null && coordinate[1] !== null &&
            Number.isFinite(Number(coordinate[0])) && Number.isFinite(Number(coordinate[1])))) {
            const coordinates = ab.slice(0, 2).map((coordinate) => [Number(coordinate[0]), Number(coordinate[1])]);
            const angle = ((bearing(point(coordinates[0]), point(coordinates[1])) + 360) % 360);
            const angleText = `${((Math.round(angle * 100) / 100) % 360).toFixed(2)}º`;
            values.push(angleText);
            labels.push(`${res.ANGULO}: ${angleText}`);
        }
        if (!values.length) return null;
        return (
            <div className="new-planning-flight-map-summary" aria-label={labels.join(', ')}>
                <strong>{values.join(' ')}</strong>
            </div>
        );
    };

    renderStrategyMapDialog = () => {
        const polygon = this.state.strategyMapPolygon;
        if (!polygon) return null;
        const controlsDisabled = this.state.strategyMapSelectingAB || this.state.strategyMapClosing;
        return (
            <Dialog
                open
                fullWidth
                maxWidth="lg"
                PaperProps={{ className: 'new-planning-dialog-paper new-planning-strategy-dialog' }}
                onClose={this.closeStrategyMap}
                aria-labelledby="new-planning-flight-dialog-title"
            >
                <DialogTitle id="new-planning-flight-dialog-title">{polygon.name}</DialogTitle>
                <DialogContent>
                    <div className="new-planning-strategy-layout">
                        <div className="new-planning-modal-map-wrap new-planning-flight-map-wrap">
                            <PolygonFlightPathMap
                                polygon={polygon}
                                jobId={this.state.strategyMapJobId}
                                contextPolygons={this.getStrategyMapContext()}
                                contextDrafts={this.state.strategyMapDrafts}
                                onPolygonSelect={this.switchStrategyMapPolygon}
                                strategy={this.state.strategyMapStrategy}
                                swathWidthM={polygon.swathWidthM}
                                base={this.state.base}
                                runways={this.state.runways}
                                baseLabel={this.text('BASE', 'Base')}
                                runwayLabel={this.text('PISTA', 'Pista')}
                                pointALabel={this.props.res.PONTO_A}
                                pointBLabel={this.props.res.PONTO_B}
                                selectingAB={this.state.strategyMapSelectingAB}
                                editingDisabled={this.state.strategyMapClosing}
                                onABChange={this.handleStrategyMapABChange}
                            />
                            {this.renderStrategyMapSummary(polygon)}
                        </div>
                        <aside className="new-planning-strategy-settings">
                            <Typography variant="subtitle2">{this.props.res.CONFIGURACOES}</Typography>
                            <FormControl variant="outlined" fullWidth disabled={controlsDisabled}>
                                <InputLabel>{this.props.res.ESTRATEGIA}</InputLabel>
                                <Select
                                    value={this.state.strategyMapStrategy}
                                    onChange={this.handleStrategyMapStrategyChange}
                                    label={this.props.res.ESTRATEGIA}
                                    renderValue={(value) => {
                                        const strategy = polygon.strategies.find((item) => item.type === value);
                                        return strategy ? this.renderStrategyMapOption(strategy) : value;
                                    }}
                                >
                                    {polygon.strategies.map((strategy) => (
                                        <MenuItem key={strategy.type} value={strategy.type}>
                                            {this.renderStrategyMapOption(strategy)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <div className="new-planning-ab-actions">
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    title={this.props.res.RESTAURAR_AB_INICIAL}
                                    disabled={controlsDisabled || !this.state.strategyMapInitialPolygon}
                                    onClick={this.restoreInitialStrategyMapAB}
                                >
                                    {this.props.res.MELHOR_RUMO}
                                </Button>
                                <Button
                                    variant={this.state.strategyMapSelectingAB ? 'contained' : 'outlined'}
                                    color="primary"
                                    aria-pressed={this.state.strategyMapSelectingAB}
                                    disabled={this.state.strategyMapClosing}
                                    onClick={() => this.setState((state) => ({ strategyMapSelectingAB: !state.strategyMapSelectingAB, strategyMapError: '' }))}
                                >
                                    {this.state.strategyMapSelectingAB ? this.props.res.CANCELAR : this.props.res.NOVO_AB}
                                </Button>
                            </div>
                            <div className="new-planning-ab-actions">
                                <Button variant="outlined" disabled={controlsDisabled} onClick={this.invertStrategyMapAB}>
                                    {this.props.res.INVERTER_AB}
                                </Button>
                                <Button variant="outlined" disabled={controlsDisabled} onClick={this.mirrorStrategyMapAB}>
                                    {this.props.res.ESPELHAR_AB}
                                </Button>
                            </div>
                            {this.state.strategyMapError && (
                                <Typography role="alert" variant="body2" color="error">{this.state.strategyMapError}</Typography>
                            )}
                        </aside>
                    </div>
                </DialogContent>
                <DialogActions className="new-planning-strategy-dialog-actions">
                    {this.state.strategyMapError && (
                        <Button
                            variant="outlined"
                            color="primary"
                            disabled={this.state.strategyMapClosing}
                            onClick={this.restoreOriginalPlanningAndClose}
                        >
                            {this.props.res.RESTAURAR_PLANEJAMENTO_E_FECHAR}
                        </Button>
                    )}
                    <Button disabled={this.state.strategyMapClosing} onClick={this.closeStrategyMap}>{this.text('FECHAR', 'Fechar')}</Button>
                </DialogActions>
            </Dialog>
        );
    };

    handleAreaSelectionChange = (areaId, selected) => this.setAreaSelection(areaId, selected);

    setAreaSelection = (areaId, selected) => this.setState((state) => {
        const id = String(areaId);
        const currentIds = state.selectedAreaIds.map(String);
        const selectedAreaIds = selected
            ? (this.props.singleAreaOnly ? [id] : Array.from(new Set(currentIds.concat(id))))
            : currentIds.filter((selectedId) => selectedId !== id);
        return { selectedAreaIds, ...this.clearGenerated() };
    });

    toggleAreaSelection = (areaId) => {
        const id = String(areaId);
        const isSelected = this.state.selectedAreaIds.some((selectedId) => String(selectedId) === id);
        this.setAreaSelection(areaId, !isSelected);
    };

    handlePolygonFlowChange = (areaId, value) => this.setState((state) => ({
        polygonFlows: Object.assign({}, state.polygonFlows, { [String(areaId)]: value }),
        ...this.clearGenerated()
    }));

    numberParameter = (plane, field, fallback) => {
        const value = plane && plane[field] !== undefined ? Number(String(plane[field]).replace(',', '.')) : NaN;
        return Number.isFinite(value) && value > 0 ? value : fallback;
    };

    hasDgps = (plane) => {
        const dgpsId = plane && (plane.id_dgps || plane.idDgps || plane.idDGPS);
        return Number(dgpsId) > 0;
    };

    getPolygonStrategyTimes = (estimate, swathWidthM) => {
        const planningState = estimate && estimate.planningJson && estimate.planningJson.state || {};
        const configuredDimension = (values) => values.map(Number)
            .find((value) => Number.isFinite(value) && value > 0) || null;
        const polygons = estimate && estimate.planningJson && estimate.planningJson.state &&
            Array.isArray(estimate.planningJson.state.polygons)
            ? estimate.planningJson.state.polygons.filter((polygon) => String(polygon.type || 'N').toUpperCase() === 'N')
            : [];
        return polygons.map((polygon, index) => {
            const options = Array.isArray(polygon.out) ? polygon.out : [];
            const selectedLineIndex = Number.isInteger(Number(polygon.selected_line))
                ? Number(polygon.selected_line)
                : 0;
            const selectedLine = Array.isArray(polygon.lines) ? polygon.lines[selectedLineIndex] : null;
            const rawLineCount = selectedLine && selectedLine.qtd !== null && selectedLine.qtd !== undefined
                ? selectedLine.qtd
                : selectedLine && Array.isArray(selectedLine.lines)
                    ? selectedLine.lines.length
                    : polygon.numberofswaths !== null && polygon.numberofswaths !== undefined
                        ? polygon.numberofswaths
                        : NaN;
            const lineCount = Number(rawLineCount);
            const flow = polygon.fluxo !== null && polygon.fluxo !== undefined && polygon.fluxo !== ''
                ? Number(polygon.fluxo)
                : NaN;
            const strategies = PLANNING_STRATEGIES.map(({ type, labelKey }) => {
                const option = options.find((item) => String(item && item.type || '').toUpperCase() === type);
                const seconds = option && option.time_total !== null && option.time_total !== undefined
                    ? Number(option.time_total)
                    : NaN;
                return { type, labelKey, seconds: Number.isFinite(seconds) ? seconds : null };
            }).sort((left, right) => {
                const leftSeconds = Number.isFinite(left.seconds) ? left.seconds : Infinity;
                const rightSeconds = Number.isFinite(right.seconds) ? right.seconds : Infinity;
                return leftSeconds - rightSeconds;
            });

            return {
                id: polygon.guid || polygon.id || `${polygon.name || 'polygon'}-${index}`,
                name: polygon.name || `${this.text('TALHOES_', 'Talhões')} ${index + 1}`,
                feature: polygon.polygon,
                areaHectares: featureAreaHectares(polygon.polygon),
                lines: selectedLine,
                ab: parsePoints(polygon.ABs),
                exclusions: estimate.planningJson.state.polygons
                    .filter((item) => item.type === 'X' || item.type === 'XL')
                    .map((item) => item.polygon).filter(Boolean),
                selectedAB: Number(polygon.selected_AB) || 0,
                selectedABBack: Number(polygon.selected_AB_back) || 0,
                lineIndex: selectedLineIndex,
                loopWidthM: configuredDimension([planningState.balloon_width, planningState.size_loop,
                    selectedLine && selectedLine.size_loop, polygon.size_loop]),
                loopLengthM: configuredDimension([planningState.balloon_length, planningState.size_h_loop,
                    selectedLine && selectedLine.size_h_loop, polygon.size_h_loop]),
                swathWidthM,
                lineCount: Number.isFinite(lineCount) && lineCount >= 0 ? lineCount : null,
                flow: Number.isFinite(flow) ? flow : null,
                selectedStrategy: String(polygon.estrategia || (options[Number(polygon.selected_Out) || 0] || {}).type || 'BACKTOBACK').toUpperCase(),
                estimateOutputs: options,
                estimateUnavailable: Boolean(polygon.estimateUnavailable),
                estimateConfig: {
                    base: estimate.planningJson.base || estimate.planningJson.state.base || null,
                    runways: estimate.planningJson.fuel || estimate.planningJson.state.fuel || [],
                    speedApp: Number(estimate.planningJson.state.application_speed || estimate.planningJson.state.speed_apl) || 110,
                    speedTra: Number(estimate.planningJson.state.travel_speed || estimate.planningJson.state.speed_travel) || 110,
                    speedB: Number(estimate.planningJson.state.balloon_speed || estimate.planningJson.state.speed_loop) || 110,
                    carga: Number(estimate.planningJson.state.hopper_load || estimate.planningJson.state.carga) || 600,
                    refuelMinutes: Number.isFinite(Number(estimate.planningJson.state.r_time))
                        ? Math.max(0, Number(estimate.planningJson.state.r_time)) : 5
                },
                strategies
            };
        });
    };

    cancelStrategyMapEstimate = () => {
        this.strategyEstimateRequest += 1;
        if (this.strategyEstimateTimer !== null) clearTimeout(this.strategyEstimateTimer);
        this.strategyEstimateTimer = null;
        if (this.strategyEstimateController) this.strategyEstimateController.abort();
        this.strategyEstimateController = null;
        this.strategyEstimatePromise = null;
    };

    scheduleStrategyMapEstimate = () => {
        this.cancelStrategyMapEstimate();
        const polygon = this.state.strategyMapPolygon;
        if (!polygon || !polygon.estimateConfig || polygon.estimateUnavailable) {
            this.setState({ strategyMapEstimating: false });
            return;
        }
        const request = this.strategyEstimateRequest;
        const strategy = this.state.strategyMapStrategy;
        this.setState({ strategyMapEstimating: true });
        this.strategyEstimateTimer = setTimeout(() => {
            this.strategyEstimateTimer = null;
            this.strategyEstimatePromise = this.loadStrategyMapEstimate(request, polygon, strategy);
        }, 300);
    };

    loadStrategyMapEstimate = async (request, polygon, strategy) => {
        try {
            const { pattern, ab } = getPolygonStrategyPlan(polygon, strategy, polygon.swathWidthM);
            if (!ab || !pattern || !pattern.lines.length) throw new Error();
            const config = polygon.estimateConfig;
            const savedBase = this.state.base || config.base;
            const base = Array.isArray(savedBase)
                ? { lat: savedBase[1], lng: savedBase[0] }
                : savedBase || { lat: ab[0][1], lng: ab[0][0] };
            const savedRunways = Array.isArray(config.runways) ? config.runways : [];
            const runways = this.state.runways.length ? this.state.runways : savedRunways.length ? savedRunways : [base];
            const body = new URLSearchParams();
            const values = {
                guid: polygon.id,
                polygon: JSON.stringify(polygon.feature),
                ab: JSON.stringify(ab),
                pointA: JSON.stringify(ab[0]),
                lines: JSON.stringify(pattern.lines),
                interPoly: JSON.stringify(polygon.exclusions || []),
                base: JSON.stringify([base.lng, base.lat]),
                fuel: JSON.stringify(runways),
                faixa: polygon.swathWidthM,
                fluxo: Number.isFinite(polygon.flow) && polygon.flow > 0 ? polygon.flow : 10,
                carga: config.carga,
                speedApp: config.speedApp,
                speedTra: config.speedTra,
                speedB: config.speedB,
                space: polygon.loopWidthM > 0 ? polygon.loopWidthM : 400,
                spaceH: polygon.loopLengthM > 0 ? polygon.loopLengthM : 600,
                planningABBack: polygon.selectedABBack === 1 ? 1 : 0,
                line: 0,
                type: '',
                cost: 0
            };
            Object.keys(values).forEach((key) => body.append(key, values[key]));
            const controller = typeof AbortController === 'function' ? new AbortController() : null;
            this.strategyEstimateController = controller;
            const timeout = controller ? setTimeout(() => controller.abort(), 30000) : null;
            let result;
            try {
                result = await jsonRequest(`${Config.server}/estimate`, {
                    method: 'POST', headers: tokenHeaders(), body,
                    signal: controller ? controller.signal : undefined
                });
            } finally {
                if (timeout !== null) clearTimeout(timeout);
            }
            if (request !== this.strategyEstimateRequest) return;
            if (!result || !Array.isArray(result.out) || !result.out.length) throw new Error();
            const times = new Map(result.out.map((option) => {
                const parts = ['appTime', 'loopTime', 'fuelTime', 'travelTime'].map((key) => option[key]);
                const groundTime = Math.max(0, Number(option.qtdFuel) || 0) * (config.refuelMinutes ?? 5) * 60;
                const seconds = parts.every((value) => Number.isFinite(value) && value >= 0)
                    ? parts.reduce((total, value) => total + value, groundTime) : null;
                return [String(option.type).toUpperCase(), seconds];
            }));
            if (![...times.values()].some(Number.isFinite)) throw new Error();
            const estimateOutputs = PLANNING_STRATEGIES.map(({ type }) => {
                const option = result.out.find((item) => String(item.type).toUpperCase() === type);
                if (!option) return { type, time_total: null };
                const seconds = times.get(type);
                const groundTime = Math.max(0, Number(option.qtdFuel) || 0) * (config.refuelMinutes ?? 5) * 60;
                return {
                    ...option,
                    type,
                    time_spray: option.appTime,
                    time_voo: Number.isFinite(seconds) ? seconds - groundTime : null,
                    time_total: seconds,
                    tVoo: Number.isFinite(seconds) ? seconds - groundTime : null,
                    trasTime: option.travelTime,
                    refuelTravelTime: option.fuelTime,
                    refuelGroundTime: groundTime,
                    travelSpeed: config.speedTra,
                    first_line: option.first
                };
            });
            const updated = {
                ...polygon,
                estimateUnavailable: false,
                estimateOutputs,
                strategies: polygon.strategies.map((item) => ({ ...item, seconds: times.get(item.type) ?? null }))
                    .sort((left, right) => (left.seconds ?? Infinity) - (right.seconds ?? Infinity))
            };
            this.strategyEstimateController = null;
            this.setState((state) => {
                const current = state.strategyMapPolygon;
                if (request !== this.strategyEstimateRequest || !current || current.lines !== polygon.lines ||
                    current.selectedAB !== polygon.selectedAB || current.selectedABBack !== polygon.selectedABBack) return null;
                return {
                    strategyMapPolygon: { ...current, estimateUnavailable: false, estimateOutputs: updated.estimateOutputs, strategies: updated.strategies },
                    strategyMapEstimating: false,
                    strategyMapError: ''
                };
            });
            return updated;
        } catch (error) {
            if (request !== this.strategyEstimateRequest) return;
            this.strategyEstimateController = null;
            const { res } = this.props;
            const message = error.code === 'LIMIT_MSG' ? res.ERRO_SALDO
                : error.code === 'LIMIT_AREA' ? res.LIMIT_AREA
                    : error.status === 405 ? res.PERMISSAO_MSG : res.ERRO_ESTIMATIVA_ESTRATEGIA;
            if (error.code === 'LIMIT_MSG') {
                const updated = {
                    ...polygon,
                    estimateUnavailable: true,
                    estimateOutputs: null,
                    strategies: polygon.strategies.map((item) => ({ ...item, seconds: null }))
                };
                this.setState({ strategyMapPolygon: updated, strategyMapEstimating: false, strategyMapError: message });
                return updated;
            }
            this.setState({ strategyMapEstimating: false, strategyMapError: message });
            return null;
        }
    };

    getStrategyMapContext = () => {
        if (this.strategyMapContextJobs === this.state.jobs) return this.strategyMapContext;
        const context = [];
        const auxiliary = new Set();
        this.state.jobs.forEach((draft) => {
            (draft.polygonStrategyTimes || []).forEach((polygon) => context.push({ polygon, type: 'N', jobId: draft.id }));
            const polygons = draft.estimate && draft.estimate.planningJson && draft.estimate.planningJson.state.polygons || [];
            polygons.filter((polygon) => !isApplicationArea(polygon)).forEach((polygon) => {
                const feature = polygon.polygon || areaToFeature(polygon);
                if (!feature) return;
                const type = String(polygon.type).toUpperCase();
                const key = `${type}:${JSON.stringify(feature.geometry)}`;
                if (auxiliary.has(key)) return;
                auxiliary.add(key);
                context.push({ polygon: { id: polygon.guid || polygon.id, name: polygon.name, feature }, type, jobId: draft.id });
            });
        });
        this.strategyMapContextJobs = this.state.jobs;
        this.strategyMapContext = context;
        return context;
    };

    getStrategyMapSelection = (polygon, draft, strategy) => {
        const initial = draft && (draft.initialPolygonStrategyTimes || []).find((item) => String(item.id) === String(polygon.id));
        const fastestStrategy = polygon.strategies.find((strategy) => Number.isFinite(strategy.seconds)) || polygon.strategies[0];
        return {
            strategyMapPolygon: polygon,
            strategyMapInitialPolygon: initial || polygon,
            strategyMapJobId: draft ? draft.id : null,
            strategyMapClosing: false,
            strategyMapStrategy: strategy || polygon.selectedStrategy || (fastestStrategy ? fastestStrategy.type : 'BACKTOBACK'),
            strategyMapSelectingAB: false,
            strategyMapError: polygon.estimateUnavailable ? this.props.res.ERRO_SALDO : '',
            strategyMapEstimating: false
        };
    };

    openStrategyMap = (polygon, jobId = null) => {
        if (this.saveInProgress) return;
        this.cancelStrategyMapEstimate();
        const draft = this.state.jobs.find((item) => item.id === jobId);
        this.setState({ ...this.getStrategyMapSelection(polygon, draft), strategyMapDrafts: {} });
    };

    switchStrategyMapPolygon = async (jobId, polygonId) => {
        if (this.strategyMapCloseInProgress || this.state.strategyMapSelectingAB) return;
        const draft = this.state.jobs.find((item) => item.id === jobId);
        const target = draft && (draft.polygonStrategyTimes || []).find((item) => String(item.id) === String(polygonId));
        if (!target || (jobId === this.state.strategyMapJobId && String(polygonId) === String(this.state.strategyMapPolygon.id))) return;
        this.strategyMapCloseInProgress = true;
        this.setState({ strategyMapClosing: true });
        try {
            const polygon = await this.settleStrategyMapEstimate();
            if (!polygon) return;
            const drafts = {
                ...this.state.strategyMapDrafts,
                [`${this.state.strategyMapJobId}:${polygon.id}`]: {
                    jobId: this.state.strategyMapJobId, polygon, strategy: this.state.strategyMapStrategy
                }
            };
            const pending = drafts[`${jobId}:${polygonId}`];
            this.cancelStrategyMapEstimate();
            this.setState({
                ...this.getStrategyMapSelection(pending ? pending.polygon : target, draft, pending && pending.strategy),
                strategyMapDrafts: drafts
            });
        } catch (_) {
            this.setState({ strategyMapError: this.props.res.ERRO_APLICAR_ESTRATEGIA });
        } finally {
            this.strategyMapCloseInProgress = false;
            this.setState({ strategyMapClosing: false });
        }
    };

    rebuildStrategyPreview = (polygon, strategy, referenceAB = null, width = polygon.swathWidthM) => {
        const candidate = polygon.lines || {};
        let abs = candidate.ABs;
        if (referenceAB || !abs || !abs.initialAB || !abs.mirroredAB) {
            const reference = referenceAB || (abs && abs.startAB && abs.startAB.c) || polygon.ab;
            abs = toolsPlan.createPlanningABPair(polygon.feature, reference, width);
        }
        if (!abs) throw new Error(this.props.res.AB_PREVIA_INVALIDO);
        const back = toolsPlan.isCentralPlanningStrategy(strategy) ? 0 : polygon.selectedABBack;
        const lines = toolsPlan.createPlanningLines(polygon.feature, abs, back, width, polygon.exclusions || []);
        if (!lines.length) throw new Error(this.props.res.AB_PREVIA_INVALIDO);
        return {
            ...polygon,
            lines: { ...candidate, ABs: abs, lines, qtd: lines.length },
            lineCount: lines.length,
            swathWidthM: width,
            previewEdited: true,
            estimateOutputs: null,
            strategies: polygon.strategies.map((item) => ({ ...item, seconds: null }))
        };
    };

    restoreInitialStrategyMapAB = () => {
        this.cancelStrategyMapEstimate();
        this.setState((state) => {
            const initial = state.strategyMapInitialPolygon;
            const current = state.strategyMapPolygon;
            if (!initial || !current) return null;
            const sameWidth = Math.abs(initial.swathWidthM - current.swathWidthM) < 0.0001;
            try {
                let polygon = {
                    ...current,
                    lines: initial.lines,
                    ab: initial.ab,
                    selectedAB: initial.selectedAB,
                    selectedABBack: initial.selectedABBack,
                    lineIndex: initial.lineIndex,
                    lineCount: initial.lineCount,
                    previewEdited: false,
                    estimateUnavailable: Boolean(initial.estimateUnavailable),
                    estimateOutputs: initial.estimateOutputs,
                    strategies: initial.strategies
                };
                if (!sameWidth) {
                    const abs = initial.lines && initial.lines.ABs;
                    const reference = abs && (abs.initialAB || abs.startAB);
                    polygon = this.rebuildStrategyPreview(polygon, state.strategyMapStrategy, reference ? reference.c : initial.ab);
                }
                return { strategyMapPolygon: polygon, strategyMapEstimating: false, strategyMapError: '' };
            } catch (_) { return { strategyMapEstimating: false, strategyMapError: this.props.res.AB_PREVIA_INVALIDO }; }
        });
    };

    handleStrategyMapStrategyChange = (event) => {
        const strategy = event.target.value;
        this.setState((state) => {
            try {
                const polygon = state.strategyMapPolygon;
                return {
                    strategyMapStrategy: strategy,
                    strategyMapPolygon: polygon.previewEdited ? this.rebuildStrategyPreview(polygon, strategy) : polygon,
                    strategyMapError: ''
                };
            } catch (_) { return { strategyMapError: this.props.res.AB_PREVIA_INVALIDO }; }
        });
    };

    handleStrategyMapABChange = (ab) => this.setState((state) => {
        try {
            const polygon = { ...state.strategyMapPolygon, selectedAB: 1, selectedABBack: 0 };
            return {
                strategyMapPolygon: this.rebuildStrategyPreview(polygon, state.strategyMapStrategy, ab),
                strategyMapSelectingAB: false,
                strategyMapError: ''
            };
        } catch (_) { return { strategyMapSelectingAB: false, strategyMapError: this.props.res.AB_PREVIA_INVALIDO }; }
    });

    invertStrategyMapAB = () => this.setState((state) => {
        const polygon = state.strategyMapPolygon;
        return {
            strategyMapPolygon: {
                ...polygon,
                selectedAB: polygon.selectedAB === 1 ? 0 : 1,
                ab: Array.isArray(polygon.ab) ? polygon.ab.slice().reverse() : polygon.ab,
                previewEdited: true,
                estimateOutputs: null,
                strategies: polygon.strategies.map((item) => ({ ...item, seconds: null }))
            },
            strategyMapError: ''
        };
    });

    mirrorStrategyMapAB = () => this.setState((state) => {
        try {
            const polygon = { ...state.strategyMapPolygon, selectedABBack: state.strategyMapPolygon.selectedABBack === 1 ? 0 : 1 };
            return { strategyMapPolygon: this.rebuildStrategyPreview(polygon, state.strategyMapStrategy), strategyMapError: '' };
        } catch (_) { return { strategyMapError: this.props.res.AB_PREVIA_INVALIDO }; }
    });

    applyStrategyMapDraft = (state, polygon, strategy) => {
        const draft = state.jobs.find((item) => item.id === state.strategyMapJobId);
        if (!draft) return state.jobs;
        const previous = (draft.polygonStrategyTimes || []).find((item) => String(item.id) === String(polygon.id));
        if (previous && previous.lines === polygon.lines && JSON.stringify(previous.ab) === JSON.stringify(polygon.ab) &&
            previous.selectedAB === polygon.selectedAB && previous.selectedABBack === polygon.selectedABBack &&
            Boolean(previous.estimateUnavailable) === Boolean(polygon.estimateUnavailable) &&
            previous.selectedStrategy === strategy) return state.jobs;

        const planningJson = draft.estimate.planningJson;
        const planningState = planningJson.state;
        const sourcePolygons = planningState.polygons;
        const sourceIndex = sourcePolygons.findIndex((item) => String(item.guid || item.id) === String(polygon.id));
        const { pattern, ab } = getPolygonStrategyPlan(polygon, strategy, polygon.swathWidthM);
        if (sourceIndex < 0 || !pattern || !pattern.lines.length || !ab) throw new Error();
        const outputs = PLANNING_STRATEGIES.map(({ type }) =>
            (polygon.estimateOutputs || []).find((item) => String(item.type).toUpperCase() === type) || { type });
        const selectedOut = PLANNING_STRATEGIES.findIndex((item) => item.type === strategy);
        const selected = outputs[selectedOut];
        if (!selected || (!polygon.estimateUnavailable && !Number.isFinite(selected.time_total))) throw new Error();
        const source = sourcePolygons[sourceIndex];
        const lines = Array.isArray(source.lines) ? source.lines.slice() : [];
        const lineIndex = Number.isInteger(polygon.lineIndex) && polygon.lineIndex >= 0 ? polygon.lineIndex : 0;
        const heading = (bearing(point(ab[0]), point(ab[1])) + 360) % 360;
        const yaw = (heading + (polygon.selectedAB === 1 ? 180 : 0)) % 360;
        lines[lineIndex] = {
            ...polygon.lines, lines: pattern.lines, ABs: pattern.ABs, qtd: pattern.lines.length, yaw,
            planningGeometryVersion: 2,
            _planningABBack: toolsPlan.isCentralPlanningStrategy(strategy) ? 0 : polygon.selectedABBack
        };
        const updatedPolygon = {
            ...source, lines, selected_line: lineIndex,
            selected_AB: polygon.selectedAB, selected_AB_back: polygon.selectedABBack,
            selected_Out: selectedOut, estrategia: strategy, estrategia_manual: true, patterntype: strategy,
            ABs: JSON.stringify(ab), ab: ab.map((coordinate) => [coordinate[1], coordinate[0]]),
            latlngpointa: JSON.stringify(ab[0]), latlngpointb: JSON.stringify(ab[1]),
            numberofswaths: pattern.lines.length, faixa: polygon.swathWidthM,
            yaw, yaw_value: yaw, planningVersion: 2, space: 0,
            estimateUnavailable: Boolean(polygon.estimateUnavailable),
            out: polygon.estimateUnavailable ? null : outputs,
            flxMedApl: polygon.estimateUnavailable ? null : Number.isFinite(selected.qtdFuel) ? selected.qtdFuel : source.flxMedApl,
            carga_final: polygon.estimateUnavailable ? null : Number.isFinite(selected.carga_atual) ? selected.carga_atual : source.carga_final
        };
        const polygons = sourcePolygons.map((item, index) => index === sourceIndex ? updatedPolygon : item);
        const selectedOutput = (item) => (item.out || []).find((option) => option.type === item.estrategia) ||
            (item.out || [])[Number(item.selected_Out) || 0] || {};
        const finite = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;
        const contributions = {
            time_spray: (item) => finite(selectedOutput(item).time_spray ?? selectedOutput(item).appTime),
            time_voo: (item) => finite(selectedOutput(item).time_voo ?? selectedOutput(item).time_total),
            time_total: (item) => finite(selectedOutput(item).time_total),
            time_loop: (item) => finite(selectedOutput(item).loopTime),
            time_tras: (item) => finite(selectedOutput(item).refuelTravelTime ?? selectedOutput(item).fuelTime),
            time_ground: (item) => finite(selectedOutput(item).refuelGroundTime),
            refuel_travel_time: (item) => finite(selectedOutput(item).refuelTravelTime ?? selectedOutput(item).fuelTime),
            qtdFuel: (item) => finite(selectedOutput(item).qtdFuel),
            flxMedApl: (item) => finite(item.flxMedApl),
            numberofswaths: (item) => finite(item.numberofswaths)
        };
        const initialEstimate = draft.initialEstimate || draft.estimate;
        const initialPolygons = initialEstimate.planningJson.state.polygons;
        const oldTotals = initialEstimate.totals || initialEstimate.planningJson.state.totals || {};
        const totals = { ...oldTotals };
        Object.keys(contributions).forEach((key) => {
            const sum = (items) => items.reduce((total, item) => total + contributions[key](item), 0);
            const previousValue = Number.isFinite(oldTotals[key]) ? oldTotals[key] : sum(initialPolygons);
            totals[key] = Math.max(0, previousValue + sum(polygons) - sum(initialPolygons));
        });
        const interAreaTime = (items) => {
            let previousEnd = null;
            let seconds = 0;
            const valid = (coordinate) => Array.isArray(coordinate) && coordinate.length >= 2 &&
                coordinate.every((value) => typeof value === 'number' && Number.isFinite(value));
            items.filter((item) => item.type === 'N').forEach((item) => {
                const option = selectedOutput(item);
                const speed = finite(option.travelSpeed) || finite(planningState.travel_speed) || finite(planningState.speed_travel);
                if (valid(previousEnd) && valid(option.startPath) && speed > 0) {
                    seconds += distance(point(previousEnd), point(option.startPath), { units: 'kilometers' }) / speed * 3600;
                }
                if (valid(option.endPath)) previousEnd = option.endPath;
            });
            return seconds;
        };
        const nextInterAreaTime = interAreaTime(polygons);
        const previousInterAreaTime = Number.isFinite(oldTotals.inter_area_travel_time)
            ? oldTotals.inter_area_travel_time : interAreaTime(initialPolygons);
        const interAreaDelta = nextInterAreaTime - previousInterAreaTime;
        ['time_total', 'time_voo', 'time_tras'].forEach((key) => { totals[key] = Math.max(0, totals[key] + interAreaDelta); });
        totals.inter_area_travel_time = nextInterAreaTime;
        const estimateUnavailable = polygons.some((item) => item.type === 'N' && item.estimateUnavailable);
        if (estimateUnavailable) {
            ['time_spray', 'time_voo', 'time_total', 'time_loop', 'time_tras', 'time_ground',
                'refuel_travel_time', 'inter_area_travel_time', 'travel_time', 'qtdFuel', 'flxMedApl']
                .forEach((key) => { totals[key] = null; });
        }
        const estimate = {
            ...draft.estimate, totals,
            estimateUnavailable,
            planningJson: {
                ...planningJson,
                state: { ...planningState, polygons, totals, time_total: totals.time_total, time_voo: totals.time_voo, time_spray: totals.time_spray }
            }
        };
        const updated = {
            ...draft, estimate, initialEstimate,
            initialPolygonStrategyTimes: draft.initialPolygonStrategyTimes || draft.polygonStrategyTimes,
            polygonStrategyTimes: this.getPolygonStrategyTimes(estimate, draft.faixa),
            hasUnsavedChanges: Boolean(draft.job) || Boolean(draft.hasUnsavedChanges)
        };
        return state.jobs.map((item) => item.id === draft.id ? updated : item);
    };

    settleStrategyMapEstimate = async () => {
        const strategy = this.state.strategyMapStrategy;
        const polygon = this.state.strategyMapPolygon;
        if (polygon.estimateUnavailable) return polygon;
        if (polygon.previewEdited && (!polygon.estimateOutputs || this.state.strategyMapEstimating)) {
            if (this.strategyEstimateTimer !== null) {
                clearTimeout(this.strategyEstimateTimer);
                this.strategyEstimateTimer = null;
                this.strategyEstimatePromise = this.loadStrategyMapEstimate(this.strategyEstimateRequest, polygon, strategy);
            } else if (!this.state.strategyMapEstimating || !this.strategyEstimatePromise) {
                this.cancelStrategyMapEstimate();
                this.setState({ strategyMapEstimating: true });
                this.strategyEstimatePromise = this.loadStrategyMapEstimate(this.strategyEstimateRequest, polygon, strategy);
            }
            return this.strategyEstimatePromise;
        }
        return polygon;
    };

    restoreOriginalPlanningAndClose = () => {
        if (this.strategyMapCloseInProgress || !this.state.strategyMapPolygon) return;
        this.cancelStrategyMapEstimate();
        this.setState((state) => ({
            jobs: state.jobs.map((draft) => {
                if (draft.id !== state.strategyMapJobId) return draft;
                const estimate = draft.initialEstimate || draft.estimate;
                return {
                    ...draft,
                    estimate,
                    polygonStrategyTimes: draft.initialPolygonStrategyTimes || this.getPolygonStrategyTimes(estimate, draft.faixa),
                    hasUnsavedChanges: Boolean(draft.hasUnsavedChanges || (draft.job && draft.estimate !== estimate))
                };
            }),
            strategyMapDrafts: {},
            strategyMapPolygon: null,
            strategyMapInitialPolygon: null,
            strategyMapJobId: null,
            strategyMapStrategy: '',
            strategyMapSelectingAB: false,
            strategyMapClosing: false,
            strategyMapEstimating: false,
            strategyMapError: ''
        }));
    };

    closeStrategyMap = async () => {
        if (this.strategyMapCloseInProgress || !this.state.strategyMapPolygon) return;
        this.strategyMapCloseInProgress = true;
        this.setState({ strategyMapClosing: true, strategyMapSelectingAB: false });
        try {
            const polygon = await this.settleStrategyMapEstimate();
            if (!polygon) return;
            const drafts = {
                ...this.state.strategyMapDrafts,
                [`${this.state.strategyMapJobId}:${polygon.id}`]: {
                    jobId: this.state.strategyMapJobId, polygon, strategy: this.state.strategyMapStrategy
                }
            };
            const jobs = Object.values(drafts).reduce((jobs, draft) => this.applyStrategyMapDraft(
                { ...this.state, jobs, strategyMapJobId: draft.jobId }, draft.polygon, draft.strategy
            ), this.state.jobs);
            this.setState({
                jobs,
                strategyMapDrafts: {},
                strategyMapPolygon: null, strategyMapInitialPolygon: null, strategyMapJobId: null,
                strategyMapStrategy: '', strategyMapSelectingAB: false, strategyMapClosing: false,
                strategyMapError: '', strategyMapEstimating: false
            });
        } catch (_) {
            this.setState({ strategyMapError: this.props.res.ERRO_APLICAR_ESTRATEGIA });
        } finally {
            this.strategyMapCloseInProgress = false;
            this.setState({ strategyMapClosing: false });
        }
    };

    generate = async () => {
        if (this.state.generating || this.saveInProgress) return;
        const polygons = this.selectedAreas();
        const applicationAreas = polygons.filter(isApplicationArea);
        const auxiliaryAreas = polygons.filter((area) => !isApplicationArea(area));
        const selectedPlanes = this.selectedPlanes();
        const client = this.selectedClient();
        if (!applicationAreas.length || !String(this.state.title || '').trim()) return;
        if (!selectedPlanes.length || !(this.numberParameter(selectedPlanes[0], 'swath_width', 0) > 0)) return;
        const defaultFlow = this.numberParameter(selectedPlanes[0], 'flow_rate', 10);

        this.generationController = typeof AbortController === 'function' ? new AbortController() : null;
        this.setState({ generating: true, ...this.clearGenerated(), generationProgress: { stage: 'prepare', batch: 1, batchTotal: 1 } });
        try {
            const jobs = [];
            let batch = 0;
            const batchTotal = selectedPlanes.length * new Set(applicationAreas.map((area) => this.numberParameter({
                flow_rate: this.state.polygonFlows[String(area.id)] || this.state.bulkFlow || defaultFlow
            }, 'flow_rate', defaultFlow))).size;
            for (const plane of selectedPlanes) {
                const faixa = this.numberParameter(plane, 'swath_width', 0);
                const groupedPolygons = {};
                applicationAreas.forEach((area) => {
                    const rawFlow = this.state.polygonFlows[String(area.id)] || this.state.bulkFlow || defaultFlow;
                    const flow = this.numberParameter({ flow_rate: rawFlow }, 'flow_rate', defaultFlow);
                    if (!groupedPolygons[flow]) groupedPolygons[flow] = [];
                    groupedPolygons[flow].push(area);
                });
                const flowGroups = Object.keys(groupedPolygons);
                for (const flowValue of flowGroups) {
                    batch += 1;
                    if (this.generationDisposed) return;
                    this.setState({ generationProgress: { stage: 'prepare', batch, batchTotal } });
                    const flow = Number(flowValue);
                    const titleParts = [this.state.title];
                    if (selectedPlanes.length > 1) titleParts.push(plane.name);
                    if (flowGroups.length > 1) titleParts.push(`${this.props.res.FLUXO} ${flow}`);
                    const title = titleParts.join(' - ');
                    const speedApp = this.numberParameter(plane, 'application_speed', 110);
                    const speedTra = this.numberParameter(plane, 'travel_speed', 110);
                    const speedB = this.numberParameter(plane, 'balloon_speed', speedApp);
                    const carga = this.numberParameter(plane, 'hopper_load', 600);
                    const estimate = await planningRequest(`${Config.server}/quick_planning`, {
                        method: 'POST',
                        headers: Object.assign({}, tokenHeaders(), { 'Content-Type': 'application/json', Accept: 'application/x-ndjson' }),
                        signal: this.generationController ? this.generationController.signal : undefined,
                        body: JSON.stringify({
                            payload: {
                                title,
                                language: this.props.res.LINGUA || 'pt-BR',
                                clientName: client ? client.name : '',
                                planeName: plane ? plane.name : '',
                                faixa,
                                fluxo: flow,
                                speedApp,
                                speedTra,
                                speedB,
                                speedAvg: (speedApp + speedTra) / 2,
                                carga,
                                medianHopperLoad: carga,
                                altura: this.numberParameter(plane, 'flight_height', 0),
                                space: Math.max(faixa + 1, this.numberParameter(plane, 'balloon_width', 400)),
                                spaceH: this.numberParameter(plane, 'balloon_length', 600),
                                base: this.state.base,
                                runway: this.state.runways[0] || null,
                                runways: this.state.runways,
                                auto: true,
                                polygons: groupedPolygons[flowValue].concat(auxiliaryAreas).map((area) => ({
                                    id: area.source === 'file' ? null : area.id,
                                    guid: area.guid || null,
                                    id_property: area.id_property || null,
                                    name: area.name,
                                    type: area.type || 'N',
                                    polygon: area.feature
                                }))
                            }
                        })
                    }, (progress) => {
                        if (!this.generationDisposed && GENERATION_STAGES.some((stage) => stage.type === progress.stage)) {
                            this.setState({ generationProgress: { ...progress, batch, batchTotal } });
                        }
                    }, this.props.res.ERRO_GERAR_PLANEJAMENTO);

                    if (this.generationDisposed) return;

                    const polygonStrategyTimes = this.getPolygonStrategyTimes(estimate, faixa);
                    jobs.push({
                        id: Tools.newGuid(),
                        plane,
                        faixa,
                        flow,
                        title: estimate.planningJson.state.title_est || title,
                        estimate,
                        initialEstimate: estimate,
                        polygonStrategyTimes,
                        initialPolygonStrategyTimes: polygonStrategyTimes,
                        job: null
                    });
                    this.setState({ jobs: jobs.slice() });
                }
            }
            this.setState({ jobs, generating: false, generationProgress: { stage: 'complete', batch, batchTotal } });
        } catch (error) {
            if (this.generationController) this.generationController.abort();
            if (!this.generationDisposed) this.setState((state) => ({
                generating: false,
                error: error.message,
                generationProgress: state.generationProgress ? { ...state.generationProgress, failed: true } : null
            }));
        } finally {
            this.generationController = null;
        }
    };

    renderGenerationProgress = () => {
        const progress = this.state.generationProgress;
        if (!progress) return null;
        const { res } = this.props;
        const active = GENERATION_STAGES.findIndex((stage) => stage.type === progress.stage);
        if (active < 0) return null;
        const count = Number.isFinite(progress.total) && progress.total > 0 && Number.isFinite(progress.completed);
        const failed = Boolean(progress.failed);
        return (
            <div className="new-planning-generation-progress">
                <div role="status" aria-live="polite" aria-atomic="true">
                    <Typography variant="subtitle2">
                        {res.PLANEJAMENTO_PROGRESSO_GRUPO.replace('{current}', String(progress.batch)).replace('{total}', String(progress.batchTotal))}
                    </Typography>
                    <Typography variant="body2">
                        {failed ? res.PLANEJAMENTO_ETAPA_ERRO : res[GENERATION_STAGES[active].label]}
                        {count && !failed ? ` (${progress.completed}/${progress.total})` : ''}
                    </Typography>
                </div>
                {this.state.generating && <LinearProgress aria-label={res[GENERATION_STAGES[active].label]} variant={count ? 'determinate' : 'indeterminate'} value={count ? progress.completed / progress.total * 100 : undefined} />}
                <ol aria-label={res.PLANEJAMENTO_ETAPAS}>
                    {GENERATION_STAGES.map((stage, index) => (
                        <li key={stage.type} className={index === active ? 'is-active' : index < active ? 'is-complete' : ''} aria-current={index === active ? 'step' : undefined}>
                            {index === active && this.state.generating
                                ? <CircularProgress size={14} aria-hidden="true" />
                                : <MaterialIcon icon={index === active && failed ? 'error_outline' : index < active || progress.stage === 'complete' ? 'check_circle' : 'radio_button_unchecked'} aria-hidden="true" />}
                            <span>{res[stage.label]}</span>
                        </li>
                    ))}
                </ol>
            </div>
        );
    };

    savePlanning = async (result) => {
        if (!result || this.saveInProgress || this.state.generating) return;
        const draft = this.state.jobs.find((item) => item.id === result.id);
        if (!draft || (draft.job && !draft.hasUnsavedChanges) || !draft.estimate || !draft.estimate.planningJson) return;

        this.saveInProgress = true;
        this.setState({ savingResultId: draft.id, error: '' });
        try {
            const body = new URLSearchParams();
            body.append('json', JSON.stringify(draft.estimate.planningJson));
            body.append('title', draft.title);
            if (draft.job && !draft.job.id_job) throw new Error(this.props.res.ERRO_APLICAR_ESTRATEGIA);
            const job = await jsonRequest(draft.job ? `${Config.server}/job/${draft.job.id_job}` : `${Config.server}/job`, {
                method: draft.job ? 'PUT' : 'POST',
                headers: tokenHeaders(),
                body
            });
            if (!job || !job.id_job_link || !job.guid_link) throw new Error(this.text('ERRO', 'Erro'));
            this.setState((state) => ({
                jobs: state.jobs.map((item) => item.id === draft.id ? Object.assign({}, item, { job, hasUnsavedChanges: false }) : item)
            }));
        } catch (error) {
            this.setState({ error: error.message });
        } finally {
            this.saveInProgress = false;
            this.setState({ savingResultId: null });
        }
    };

    sendToPlane = async (result) => {
        if (!result || !result.plane || !this.hasDgps(result.plane) || !result.job || result.hasUnsavedChanges) return;
        this.setState({ sending: true, error: '' });
        const body = new URLSearchParams();
        body.append('list_plane', JSON.stringify([result.plane.id]));
        body.append('guid', String(result.job.guid_link));
        body.append('type', 'job');
        try {
            await jsonRequest(`${Config.server}/transfer`, { method: 'POST', headers: tokenHeaders(), body });
            notification.success({ message: this.text('ENVIAR', 'Enviar') + ' ' + this.text('AERONAVE', 'aeronave') });
        } catch (error) {
            this.setState({ error: error.message });
        } finally {
            this.setState({ sending: false });
        }
    };

    download = async (result) => {
        if (!result || !result.job || result.hasUnsavedChanges) return;
        this.setState({ downloading: true, error: '' });
        try {
            const response = await fetch(`${Config.server}/job_link_download/${result.job.id_job_link}`, { headers: tokenHeaders() });
            if (!response.ok) throw new Error(`Falha no download (${response.status})`);
            const blob = await response.blob();
            const filename = String(result.title || 'planejamento').replace(/[\\/:*?"<>|]/g, '_');
            saveAs(blob, `${filename}.zjob`);
        } catch (error) {
            this.setState({ error: error.message });
        } finally {
            this.setState({ downloading: false });
        }
    };

    render() {
        const { res } = this.props;
        const selectedAreas = this.selectedAreas();
        const selectedPlanes = this.selectedPlanes();
        const selectedPlane = selectedPlanes[0];
        const swathConfigured = Boolean(selectedPlane && this.numberParameter(selectedPlane, 'swath_width', 0) > 0);
        const applicationAreas = selectedAreas.filter(isApplicationArea);
        const canGenerate = swathConfigured && applicationAreas.length > 0 && Boolean(String(this.state.title || '').trim());
        const selectedClient = this.selectedClient();
        const selectedAreaSource = this.state.clientId === IMPORTED_CLIENT_ID
            ? this.text('IMPORTADO', 'Importado')
            : selectedClient && selectedClient.name;
        const areaLabel = selectedAreas.length
            ? `${selectedAreas.length} ${this.text('TALHOES_', 'Talhões')}`
            : this.props.singleAreaOnly
                ? this.text('ESCOLHER_TALHAO', 'ESCOLHER TALHÃO')
                : this.text('ESCOLHER_TALHOES', 'ESCOLHER TALHÕES');
        const defaultFlow = this.numberParameter(selectedPlanes[0], 'flow_rate', 10);
        const flowValues = Array.from(new Set(applicationAreas.map((area) => {
            const rawFlow = this.state.polygonFlows[String(area.id)] || this.state.bulkFlow || defaultFlow;
            return this.numberParameter({ flow_rate: rawFlow }, 'flow_rate', defaultFlow);
        })));
        const flowSummary = flowValues.length
            ? `${res.FLUXO}: ${flowValues.map((flow) => `${flow.toLocaleString(res.LINGUA || undefined, { maximumFractionDigits: 2 })} ${res.LHA}`).join(', ')}`
            : `${res.CLIENTE} / ${this.text('TALHOES_', 'Talhões')} / ${res.FLUXO}`;
        const areaSummary = [areaLabel, selectedAreaSource].filter(Boolean).join(' · ');
        return (
            <main className="new-planning-page">
                <header className="new-planning-header">
                    <div>
                        <Typography variant="h4">
                            {this.props.singleAreaOnly
                                ? this.text('NOVO_PLANEJAMENTO', 'Novo Planejamento')
                                : this.text('NOVO_PLANEJAMENTO_PLUS', 'Novo Planejamento +')}
                        </Typography>
                    </div>
                </header>

                {this.state.error && <div className="new-planning-error" role="alert">{this.state.error}</div>}

                <div className="new-planning-layout">
                    <Paper className="new-planning-sidebar" elevation={1}>
                        <div className="new-planning-sidebar-heading">
                            <Typography variant="subtitle1">{this.text('DADOS_PLANEJAMENTO', 'Dados do planejamento')}</Typography>
                            {this.state.loadingOptions && <CircularProgress size={18} />}
                        </div>
                        <div className="new-planning-steps">
                            <div className={`new-planning-step ${swathConfigured ? 'is-complete' : ''}`}>
                                <div className="new-planning-step-choice">
                                    <span className="new-planning-step-number">1</span>
                                    {this.renderChoiceButton(
                                        'aircraft',
                                        'flight_takeoff',
                                        selectedPlane && !selectedPlane.name ? res.FAIXA : res.AERONAVE,
                                        selectedPlane
                                            ? `${selectedPlane.name ? `${selectedPlane.name} · ` : ''}${this.state.aircraftConfiguration.swath_width} ${Units.getLengthUnit(res)}`
                                            : this.text('ESCOLHER_AERONAVE', 'Selecionar aeronave')
                                    )}
                                </div>
                            </div>
                            <div className={`new-planning-step ${selectedAreas.length ? 'is-complete' : ''}`}>
                                <div className="new-planning-step-choice">
                                    <span className="new-planning-step-number">2</span>
                                    {this.renderChoiceButton('polygons', 'layers', flowSummary, areaSummary)}
                                </div>
                            </div>
                            <div className={`new-planning-step ${this.state.title ? 'is-complete' : ''}`}>
                                <div className="new-planning-step-choice">
                                    <span className="new-planning-step-number">3</span>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        label={this.text('TITULO', 'Título')}
                                        value={this.state.title}
                                        onChange={this.handleField('title')}
                                        inputProps={{ maxLength: 99 }}
                                    />
                                </div>
                            </div>
                        </div>

                        <Button
                            className="new-planning-generate"
                            fullWidth
                            variant="contained"
                            color="primary"
                            disabled={!canGenerate || this.state.generating || Boolean(this.state.savingResultId) || this.state.loadingOptions}
                            onClick={this.generate}
                        >
                            {this.state.generating && <CircularProgress size={18} color="inherit" />}
                            {this.state.generating ? this.text('GERANDO', 'Gerando...') : `${res.GERAR || 'Gerar'} ${this.text('PLANEJAMENTO', 'planejamento').toLowerCase()}`}
                        </Button>
                        {this.renderGenerationProgress()}
                        {!canGenerate && <Typography className="new-planning-hint" variant="caption">{this.text('MINIMO_PLANEJAMENTO', 'Informe a faixa e selecione ao menos um talhão.')}</Typography>}
                    </Paper>

                    <section className="new-planning-workspace">
                        {this.state.jobs.map((result) => (
                            <Paper key={result.id} className="new-planning-result" elevation={1}>
                                <div className="new-planning-result-details">
                                    <div>
                                        <Typography variant="overline">{result.plane && result.plane.name ? result.plane.name : result.title}</Typography>
                                        <Typography variant="body2">
                                            {res.FAIXA}: {Units.convertLength(result.faixa, 'm', res).toLocaleString(res.LINGUA || undefined, { maximumFractionDigits: 2 })} {Units.getLengthUnit(res)}
                                        </Typography>
                                        <Typography variant="body2">{res.FLUXO}: {result.flow}</Typography>
                                        {!result.estimate.estimateUnavailable && (
                                            <>
                                                <Typography variant="caption" display="block">{this.text('TEMPO_ESTIMADO', 'Tempo Total Estimado')}</Typography>
                                                <Typography variant="h4">{formatDuration(result.estimate && result.estimate.totals && result.estimate.totals.time_total)}</Typography>
                                            </>
                                        )}
                                        {result.estimate && result.estimate.fallbackUsed && (
                                            <Typography variant="caption" color="textSecondary">
                                                {this.text('PONTO_TALHAO_USADO', 'Ponto do talhão usado para base ou pista.')}
                                            </Typography>
                                        )}
                                    </div>
                                    {result.polygonStrategyTimes && result.polygonStrategyTimes.length > 0 && (
                                        <div className="new-planning-strategies">
                                            <Typography variant="caption" display="block">
                                                {this.text('TEMPOS_POR_ESTRATEGIA', 'Tempos por Estratégia do Talhão')}
                                            </Typography>
                                            <div className="new-planning-polygon-strategy-list">
                                                {result.polygonStrategyTimes.map((polygon) => (
                                                    <button
                                                        type="button"
                                                        className="new-planning-polygon-strategy-row"
                                                        key={polygon.id}
                                                        disabled={Boolean(this.state.savingResultId) || this.state.generating}
                                                        onClick={() => this.openStrategyMap(polygon, result.id)}
                                                        aria-label={`${polygon.name} · ${this.text('TEMPOS_POR_ESTRATEGIA', 'Tempos por Estratégia do Talhão')}`}
                                                    >
                                                        <PolygonPreview feature={polygon.feature} lines={polygon.lines} name={polygon.name} />
                                                        <div className="new-planning-polygon-strategy-details">
                                                            <div className="new-planning-polygon-heading">
                                                                <Typography variant="subtitle2">{polygon.name}</Typography>
                                                            </div>
                                                            {(Number.isFinite(polygon.areaHectares) || Number.isFinite(polygon.flow) || Number.isFinite(polygon.lineCount)) && (
                                                                <div className="new-planning-polygon-meta">
                                                                    {Number.isFinite(polygon.areaHectares) && (
                                                                        <Typography variant="caption" color="textSecondary" component="span">
                                                                            {polygon.areaHectares.toLocaleString(res.LINGUA || undefined, { maximumFractionDigits: 2 })} {res.HA || 'ha'}
                                                                        </Typography>
                                                                    )}
                                                                    {Number.isFinite(polygon.lineCount) && (
                                                                        <Typography variant="caption" color="textSecondary" component="span">
                                                                            {polygon.lineCount} {polygon.lineCount === 1 ? this.text('FAIXA_UNIDADE', 'faixa') : res.FAIXAS}
                                                                        </Typography>
                                                                    )}
                                                                    {Number.isFinite(polygon.flow) && (
                                                                        <Typography variant="caption" color="textSecondary" component="span">
                                                                            {res.FLUXO}: {polygon.flow.toLocaleString(res.LINGUA || undefined, { maximumFractionDigits: 2 })} {res.LHA}
                                                                        </Typography>
                                                                    )}
                                                                </div>
                                                            )}
                                                            <div className="new-planning-strategy-list">
                                                                {polygon.strategies.map((strategy, index) => (
                                                                    <div
                                                                        key={strategy.type}
                                                                        className={`new-planning-strategy-item${index === 0 && Number.isFinite(strategy.seconds) ? ' is-fastest' : ''}`}
                                                                    >
                                                                        <span>{this.text(strategy.labelKey, strategy.type)}</span>
                                                                        {Number.isFinite(strategy.seconds) && <strong>{formatDuration(strategy.seconds)}</strong>}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="new-planning-result-actions">
                                    {!result.job || result.hasUnsavedChanges ? (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            disabled={this.state.generating || Boolean(this.state.savingResultId)}
                                            onClick={() => this.savePlanning(result)}
                                        >
                                            {this.state.savingResultId === result.id && <CircularProgress size={18} color="inherit" />}
                                            {this.state.savingResultId === result.id ? this.text('SALVANDO', 'Salvando...') : this.text('SALVAR', 'Salvar')}
                                        </Button>
                                    ) : (
                                        <>
                                            {result.plane && this.hasDgps(result.plane) && (
                                                <Button variant="contained" color="primary" disabled={this.state.sending} onClick={() => this.sendToPlane(result)}>
                                                    {this.state.sending ? <CircularProgress size={18} color="inherit" /> : null}
                                                    {this.text('ENVIAR', 'Enviar')} {this.text('PARA_AERONAVE', 'para aeronave')}
                                                </Button>
                                            )}
                                            <Button variant="outlined" color="primary" disabled={this.state.downloading} onClick={() => this.download(result)}>
                                                {this.state.downloading ? <CircularProgress size={18} /> : null}
                                                {this.text('BAIXAR', 'Baixar')} .zjob
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </Paper>
                        ))}
                    </section>
                </div>
                {this.renderSelectionDialog()}
                {this.renderStrategyMapDialog()}
            </main>
        );
    }
}

export default NewPlanning;
